"use client";

import { posApi } from "@/api/pos-api";
import { useAuth } from "@/auth/AuthProvider";
import { AdminView } from "@/components/pos/admin-view";
import { CancelReservationModal } from "@/components/pos/CancelReservationModal";
import { CashierView } from "@/components/pos/cashier-view";
import { KitchenView } from "@/components/pos/kitchen-view";
import { OrderPanel } from "@/components/pos/order-panel";
import type { AdminTab } from "@/components/pos/pos-layout";
import { POSLayout } from "@/components/pos/pos-layout";
import { TablesView } from "@/components/pos/tables-view";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import {
  mergeOfflineQueueMutation,
  useLocalStorageJsonState,
} from "@/lib/offline-queue";
import {
  categories as demoCategories,
  orders as demoOrders,
  products as demoProducts,
  tables as demoTables,
  currentUser as demoUser,
} from "@/lib/pos-data";
import type {
  ActivityLog,
  Category,
  Customer,
  Order,
  OrderItem,
  PaymentMethod,
  PrintTicketTiming,
  Product,
  Reservation,
  StationPrinterConfig,
  Table,
  User,
  Zone,
} from "@/lib/pos-types";
import { computeOrderTotals } from "@/lib/totals";
import { formatPrice, cn, toMs } from "@/lib/utils";
import { Download, Plus, Clock, ChefHat, ShoppingBag, CheckCircle2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

type View = "tables" | "kitchen" | "bar" | "cashier" | "admin";

const minutesAgo = (dateInput: Date | string | undefined | null) => {
  if (!dateInput) return null;
  const d = typeof dateInput === "string" ? new Date(dateInput) : dateInput;
  if (isNaN(d.getTime())) return null;
  const diffMs = Date.now() - d.getTime();
  const diffMins = Math.max(0, Math.floor(diffMs / 60000));
  return `${diffMins} min`;
};

type POSPageProps = {
  view?: View;
  onViewChange?: (view: View) => void;
};

type QueuedMutation =
  | { id: string; kind: "createTable"; payload: unknown; createdAt: string }
  | {
      id: string;
      kind: "updateTable";
      tableId: string;
      payload: unknown;
      createdAt: string;
    }
  | { id: string; kind: "deleteTable"; tableId: string; createdAt: string }
  | {
      id: string;
      kind: "updateOrder";
      orderId: string;
      payload: unknown;
      createdAt: string;
    }
  | { id: string; kind: "createOrder"; payload: unknown; createdAt: string }
  | { id: string; kind: "createPayment"; payload: unknown; createdAt: string }
  | {
      id: string;
      kind: "updateProduct";
      productId: string;
      payload: Parameters<typeof posApi.updateProduct>[1];
      createdAt: string;
    };

/**
 * Pantalla POS unificada (multiroles).
 *
 * Responsabilidades principales:
 * - Routing por rol (tables/kitchen/bar/cashier/admin).
 * - Polling de datos (core/aux) con frecuencias por rol.
 * - Tolerancia a offline:
 *   - cola de mutaciones en localStorage
 *   - reintentos automáticos (flush)
 *   - warnings cuando algo crítico queda pendiente (ej. envío a cocina)
 * - Reconciliación de órdenes (evita que el polling pise cambios locales recientes).
 *
 * Nota: el backend es fuente de verdad para totales/precios y para autorizaciones (RBAC).
 */
type ScheduledKitchenSend = {
  id: string;
  tableId: string;
  orderId?: string;
  reservationId?: string;
  executeAt: string;
  createdAt: string;
};

const OFFLINE_QUEUE_KEY = "pos_offline_queue_v1";
const SCHEDULED_KITCHEN_SEND_KEY = "pos_scheduled_kitchen_send_v1";
const getDefaultViewForRole = (
  role: "waiter" | "kitchen" | "cashier" | "admin" | "auditor",
): View => {
  switch (role) {
    case "waiter":
      return "tables";
    case "kitchen":
      return "kitchen";
    case "cashier":
      return "cashier";
    case "admin":
    case "auditor":
      return "admin";
    default:
      return "tables";
  }
};

export default function POSPage({ view, onViewChange }: POSPageProps) {
  const { user: authedUser, logout } = useAuth();
  const apiEnabledStatic =
    typeof import.meta.env.VITE_API_BASE_URL === "string" &&
    import.meta.env.VITE_API_BASE_URL.trim().length > 0;
  const currentUser =
    (authedUser as User | null) ??
    (apiEnabledStatic ? null : (demoUser as User));
  const currentUserSafe = useMemo<User>(
    () => currentUser ?? ({ id: "", name: "", role: "waiter" } as User),
    [currentUser],
  );
  const currentUserRole = currentUserSafe.role as
    | "waiter"
    | "kitchen"
    | "cashier"
    | "admin"
    | "auditor";
  const isAdminUser = currentUserRole === "admin" || currentUserRole === "auditor";
  const isControlled = view !== undefined && typeof onViewChange === "function";
  const [activeViewInternal, setActiveViewInternal] = useState<View>(
    () => view ?? getDefaultViewForRole(currentUserRole),
  );
  const activeView = isControlled ? view : activeViewInternal;
  const [adminTab, setAdminTab] = useState<AdminTab>("dashboard");
  const [selectedTableId, setSelectedTableId] = useState<string | null>(null);
  const [selectedTakeawayOrderId, setSelectedTakeawayOrderId] =
  useState<string | null>(null);
  const [selectedReservationId, setSelectedReservationId] = useState<
    string | null
  >(null);
  const [openTableCandidate, setOpenTableCandidate] = useState<Table | null>(
    null,
  );
  const [openTakeawayClient, setOpenTakeawayClient] = useState<boolean>(
    false
  )
  const [takeawayClientName, setTakeawayClientName] = useState("")
  const [openTableDinersText, setOpenTableDinersText] = useState("");
  const [reservationActionCandidate, setReservationActionCandidate] =
    useState<Table | null>(null);
  const [reserveTableCandidate, setReserveTableCandidate] =
    useState<Table | null>(null);
  const [reservationIsTableless, setReservationIsTableless] = useState(false);
  const [isReservationModalOpen, setIsReservationModalOpen] = useState(false);
  const [reservationPrintTicketTiming, setReservationPrintTicketTiming] =
    useState<PrintTicketTiming>("on_assign_or_due");
  const [editReservationPrintTicketTiming, setEditReservationPrintTicketTiming] =
    useState<PrintTicketTiming>("on_assign_or_due");
  const [assignTableReservationCandidate, setAssignTableReservationCandidate] =
    useState<Reservation | null>(null);
  const [confirmOccupiedTableInPos, setConfirmOccupiedTableInPos] =
    useState<Table | null>(null);
  const [reservationReserverName, setReservationReserverName] = useState("");
  const [reservationReserverPhone, setReservationReserverPhone] = useState("");
  const [reservationReserverEmail, setReservationReserverEmail] = useState("");
  const [reservationPartySizeText, setReservationPartySizeText] = useState("");
  const [reservationReservedAtText, setReservationReservedAtText] =
    useState("");
  const [reservationPreBlockMinutesText, setReservationPreBlockMinutesText] =
    useState("15");
  const [reservationUnlockCodeText, setReservationUnlockCodeText] =
    useState("");
  const [
    reservationKitchenLeadMinutesText,
    setReservationKitchenLeadMinutesText,
  ] = useState("0");
  const [reservationSendMode, setReservationSendMode] =
    useState<Reservation["sendMode"]>("auto");
  const [reservationNotes, setReservationNotes] = useState("");
  const [reservationsData, setReservationsData] = useState<Reservation[]>([]);
  const [showReservationHistory, setShowReservationHistory] = useState(false);
  const [reservationsHistoryFromDate, setReservationsHistoryFromDate] =
    useState(() => {
      const d = new Date();
      d.setDate(d.getDate() - 30);
      const pad = (n: number) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    });
  const [reservationsHistoryToDate, setReservationsHistoryToDate] = useState(
    () => {
      const d = new Date();
      const pad = (n: number) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    },
  );
  const [reservationsHistoryStatus, setReservationsHistoryStatus] = useState<
    "all" | Reservation["status"]
  >("all");
  const [reservationsHistoryData, setReservationsHistoryData] = useState<
    Reservation[]
  >([]);
  const [reservationsHistoryLoading, setReservationsHistoryLoading] =
    useState(false);
  const [unlockReservedTableCandidate, setUnlockReservedTableCandidate] =
    useState<Table | null>(null);
  const [unlockReservedTableCodeText, setUnlockReservedTableCodeText] =
    useState("");
  const [transferOriginTable, setTransferOriginTable] = useState<Table | null>(null);
  const [transferSelectedZone, setTransferSelectedZone] = useState<string>("");
  const [transferDestinationTableId, setTransferDestinationTableId] = useState<string>("");
  const [isTransferring, setIsTransferring] = useState<boolean>(false);

  useEffect(() => {
    if (transferOriginTable) {
      setTransferSelectedZone(transferOriginTable.zone);
    } else {
      setTransferSelectedZone("");
    }
    setTransferDestinationTableId("");
  }, [transferOriginTable]);

  const [editReservationCandidate, setEditReservationCandidate] =
    useState<Reservation | null>(null);
  const [cancelReservationCandidate, setCancelReservationCandidate] =
    useState<Reservation | null>(null);
  const [editReservationReserverName, setEditReservationReserverName] =
    useState("");
  const [editReservationReserverPhone, setEditReservationReserverPhone] =
    useState("");
  const [editReservationReserverEmail, setEditReservationReserverEmail] =
    useState("");
  const [editReservationPartySizeText, setEditReservationPartySizeText] =
    useState("");
  const [editReservationReservedAtText, setEditReservationReservedAtText] =
    useState("");
  const [
    editReservationPreBlockMinutesText,
    setEditReservationPreBlockMinutesText,
  ] = useState("15");
  const [
    editReservationKitchenLeadMinutesText,
    setEditReservationKitchenLeadMinutesText,
  ] = useState("0");
  const [editReservationSendMode, setEditReservationSendMode] =
    useState<Reservation["sendMode"]>("auto");
  const [editReservationNotes, setEditReservationNotes] = useState("");
  const [apiOnline, setApiOnline] = useState(true);
  const lastApiErrorToastAtRef = useRef(0);
  const refreshInFlightRef = useRef(false);
  const refreshCoreInFlightRef = useRef(false);
  const refreshAuxInFlightRef = useRef(false);
  const flushInFlightRef = useRef(false);
  const offlineWarnedIdsRef = useRef<Set<string>>(new Set());
  const pendingOrdersRef = useRef<
    Map<string, { order: Order; updatedAtMs: number }>
  >(new Map());
  const reservationUnlockBypassRef = useRef<Map<string, number>>(new Map());
  const debouncedDraftTimersRef = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const apiEnabled = useMemo(() => {
    const viteBase = import.meta.env.VITE_API_BASE_URL;
    return typeof viteBase === "string" && viteBase.trim().length > 0;
  }, []);

  const toDateTimeLocalValue = useCallback((date: Date) => {
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }, []);

  const applyOrdersFromApi = useCallback((ordersNext: Order[]) => {
    const pending = pendingOrdersRef.current;
    if (pending.size === 0) return ordersNext;
    const byId = new Map(ordersNext.map((o) => [o.id, o] as const));
    const toleranceMs = 250;
    const ttlMs = 3_000;
    const nowMs = Date.now();
    for (const [id, p] of pending) {
      const apiOrder = byId.get(id) ?? null;
      const apiUpdatedAtMs = apiOrder?.updatedAt?.getTime?.() ?? 0;
      const isFreshPending = nowMs - p.updatedAtMs < ttlMs;
      const shouldKeep = apiOrder
        ? (isFreshPending || apiUpdatedAtMs < p.updatedAtMs - toleranceMs)
        : isFreshPending;

      if (shouldKeep) {
        byId.set(id, p.order);
      } else {
        pending.delete(id);
      }
    }
    return Array.from(byId.values()).sort(
      (a, b) => b.updatedAt.getTime() - a.updatedAt.getTime(),
    );
  }, []);

  const [tablesData, setTablesData] = useState<Table[]>(() =>
    apiEnabledStatic ? [] : demoTables,
  );
  const [ordersData, setOrdersData] = useState<Order[]>(() =>
    apiEnabledStatic ? [] : demoOrders,
  );
  const [productsData, setProductsData] = useState<Product[]>(() =>
    apiEnabledStatic ? [] : demoProducts,
  );
  const [categoriesData, setCategoriesData] = useState<Category[]>(() =>
    apiEnabledStatic ? [] : demoCategories,
  );
  const [activityLogsData, setActivityLogsData] = useState<ActivityLog[]>([]);
  const [zonesData, setZonesData] = useState<Zone[]>([]);
  const [stationPrintersData, setStationPrintersData] = useState<
    StationPrinterConfig[]
  >([]);
  const [offlineQueue, setOfflineQueue] = useLocalStorageJsonState<
    QueuedMutation[]
  >(OFFLINE_QUEUE_KEY, []);
  const [scheduledKitchenSends, setScheduledKitchenSends] = useState<
    ScheduledKitchenSend[]
  >(() => {
    if (typeof window === "undefined") return [];
    try {
      const raw = window.localStorage.getItem(SCHEDULED_KITCHEN_SEND_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed as ScheduledKitchenSend[];
    } catch {
      return [];
    }
  });

  const [reservationSubmitting, setReservationSubmitting] = useState(false);
  const persistedOrderIdsRef = useRef<Set<string>>(new Set());

  const createId = useCallback((prefix: string) => {
    const random =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    return `${prefix}-${random}`;
  }, []);

  const generateUnlockCode = useCallback(() => {
    const n = Math.floor(100000 + Math.random() * 900000);
    return String(n);
  }, []);

  const computeTotals = useCallback(
    (items: OrderItem[]) => computeOrderTotals(items),
    [],
  );

  const appendActivity = useCallback(
    (entry: Omit<ActivityLog, "id" | "timestamp">) => {
      const log: ActivityLog = {
        id: createId("act"),
        timestamp: new Date(),
        ...entry,
      };
      setActivityLogsData((prev) => [log, ...prev]);
    },
    [createId],
  );

  const getApiErrorInfo = useCallback((error: unknown) => {
    const errObj =
      error && typeof error === "object"
        ? (error as Record<string, unknown>)
        : null;
    const resp =
      errObj && errObj.response && typeof errObj.response === "object"
        ? (errObj.response as Record<string, unknown>)
        : null;
    const status = resp && typeof resp.status === "number" ? resp.status : null;
    const data =
      resp && resp.data && typeof resp.data === "object"
        ? (resp.data as Record<string, unknown>)
        : null;
    const message =
      (data && typeof data.message === "string" && data.message) ||
      (error instanceof Error ? error.message : "Error desconocido");
    return { status, message };
  }, []);

  const reportApiError = useCallback(
    (error: unknown, options?: { force?: boolean }) => {
      const { status, message } = getApiErrorInfo(error);
      setApiOnline(status !== null);
      const now = Date.now();
      const isUserActionError = status === 409 || status === 422 || options?.force === true;

      if (isUserActionError || now - lastApiErrorToastAtRef.current > 30_000) {
        if (!isUserActionError) {
          lastApiErrorToastAtRef.current = now;
        }
        if (status === null) {
          toast.error("API sin conexión", { description: message });
        } else if (status === 401) {
          toast.error("Sesión expirada", {
            description: "Vuelve a iniciar sesión",
          });
        } else if (status === 409 || status === 422) {
          toast.error(message);
        } else {
          toast.error("Error de API", { description: `${status} ${message}` });
        }
      }
    },
    [getApiErrorInfo],
  );

  function mergeOrders(prev: Order[], incoming: Order[]): Order[] {
    const incomingMap = new Map(incoming.map((o) => [o.id, o]));
    const merged = incoming.map((serverOrder) => {
      const local = prev.find((o) => o.id === serverOrder.id);
      if (!local) return serverOrder;
      if (pendingOrdersRef.current.has(serverOrder.id)) {
        return local;
      }
      return serverOrder;
    });

    const mergedMap = new Map(merged.map((o) => [o.id, o]));

    // Incluir únicamente órdenes locales en vuelo que aún no han sido procesadas por el servidor
    for (const localOrder of prev) {
      if (!mergedMap.has(localOrder.id) && pendingOrdersRef.current.has(localOrder.id)) {
        merged.push(localOrder);
      }
    }

    return merged;
  }

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(
        SCHEDULED_KITCHEN_SEND_KEY,
        JSON.stringify(scheduledKitchenSends),
      );
    } catch {
      return;
    }
  }, [scheduledKitchenSends]);

  const enqueueMutation = useCallback(
    (mutation: QueuedMutation) => {
      setOfflineQueue((prev) => mergeOfflineQueueMutation(prev, mutation));

      toast.warning("Acción guardada sin conexión", {
        description: `Pendiente de sincronización (${mutation.kind})`,
      });
    },
    [setOfflineQueue],
  );

  const refreshFromApi = useCallback(async () => {
    if (!apiEnabled) {
      setApiOnline(true);
      return;
    }
    if (refreshInFlightRef.current) return;
    refreshInFlightRef.current = true;
    try {
      const results = await Promise.allSettled([
        posApi.listTables(),
        posApi.listOrders({ active: true, limit: 300 }),
        posApi.listProducts(),
        posApi.listCategories(),
        posApi.listActivities(),
        posApi.listZones(),
        isAdminUser
          ? posApi.listStationPrinters()
          : Promise.resolve([] as StationPrinterConfig[]),
      ]);

      const [
        tablesRes,
        ordersRes,
        productsRes,
        categoriesRes,
        activitiesRes,
        zonesRes,
        stationPrintersRes,
      ] = results;

      if (tablesRes.status === "fulfilled") setTablesData(tablesRes.value);
      if (ordersRes.status === "fulfilled") {
        ordersRes.value.forEach((o) => persistedOrderIdsRef.current.add(o.id));
        setOrdersData(applyOrdersFromApi(ordersRes.value));
      }
      if (productsRes.status === "fulfilled")
        setProductsData(productsRes.value);
      if (categoriesRes.status === "fulfilled")
        setCategoriesData(categoriesRes.value);
      if (activitiesRes.status === "fulfilled")
        setActivityLogsData(activitiesRes.value);
      if (zonesRes.status === "fulfilled") setZonesData(zonesRes.value);
      if (stationPrintersRes.status === "fulfilled")
        setStationPrintersData(stationPrintersRes.value);

      const anyFulfilled = results.some((r) => r.status === "fulfilled");
      if (anyFulfilled) setApiOnline(true);
      else
        reportApiError(
          results[0].status === "rejected" ? results[0].reason : undefined,
        );
    } finally {
      refreshInFlightRef.current = false;
    }
  }, [apiEnabled, applyOrdersFromApi, isAdminUser, reportApiError]);

  const refreshCoreFromApi = useCallback(async () => {
    if (!apiEnabled) {
      setApiOnline(true);
      return;
    }

    if (refreshCoreInFlightRef.current) return;
    refreshCoreInFlightRef.current = true;
    try {
      const since = activityLogsData[0]?.timestamp?.toISOString?.() ?? "";
      const results = await Promise.allSettled([
        posApi.listTables(),
        posApi.listOrders({ active: true, limit: 300 }),
        Promise.resolve([] as ActivityLog[]),
      ]);

      const [tablesRes, ordersRes, activitiesRes] = results;
      if (tablesRes.status === "fulfilled") setTablesData(tablesRes.value);
      if (ordersRes.status === "fulfilled") {
        ordersRes.value.forEach((o) => persistedOrderIdsRef.current.add(o.id));
        const incoming = applyOrdersFromApi(ordersRes.value);

        setOrdersData((prev) => mergeOrders(prev, incoming));
      }
      if (activitiesRes.status === "fulfilled") {
        const incoming = activitiesRes.value;
        if (!since) {
          setActivityLogsData(incoming);
        } else if (incoming.length > 0) {
          setActivityLogsData((prev) => {
            const seen = new Set(prev.map((x) => x.id));
            const next = [...incoming.filter((x) => !seen.has(x.id)), ...prev];
            return next.slice(0, 500);
          });
        }
      }

      const anyFulfilled = results.some((r) => r.status === "fulfilled");
      if (anyFulfilled) setApiOnline(true);
      else
        reportApiError(
          results[0].status === "rejected" ? results[0].reason : undefined,
        );
    } finally {
      refreshCoreInFlightRef.current = false;
    }
  }, [activityLogsData, apiEnabled, applyOrdersFromApi, reportApiError]);

  const refreshAuxFromApi = useCallback(async () => {
    if (!apiEnabled) {
      setApiOnline(true);
      return;
    }
    if (refreshAuxInFlightRef.current) return;
    refreshAuxInFlightRef.current = true;
    try {
      const results = await Promise.allSettled([
        posApi.listProducts(),
        posApi.listCategories(),

        posApi.listZones(),
        isAdminUser
          ? posApi.listStationPrinters()
          : Promise.resolve([] as StationPrinterConfig[]),
      ]);

      const [
        productsRes,
        categoriesRes,
        zonesRes,
        stationPrintersRes,
      ] = results;
      if (productsRes.status === "fulfilled")
        setProductsData(productsRes.value);
      if (categoriesRes.status === "fulfilled")
        setCategoriesData(categoriesRes.value);
     
      if (zonesRes.status === "fulfilled") setZonesData(zonesRes.value);
      if (stationPrintersRes.status === "fulfilled")
        setStationPrintersData(stationPrintersRes.value);

      const anyFulfilled = results.some((r) => r.status === "fulfilled");
      if (anyFulfilled) setApiOnline(true);
      else
        reportApiError(
          results[0].status === "rejected" ? results[0].reason : undefined,
        );
    } finally {
      refreshAuxInFlightRef.current = false;
    }
  }, [apiEnabled,  isAdminUser, reportApiError]);

  const refreshReservationsFromApi = useCallback(async () => {
    if (!apiEnabled) return;
    if (!isAdminUser) return;
    try {
      const list = await posApi.listReservations({ limit: 50 });
      setReservationsData(list);
    } catch (error) {
      reportApiError(error);
    }
  }, [apiEnabled, isAdminUser, reportApiError]);

  const refreshReservationsHistoryFromApi = useCallback(async () => {
    if (!apiEnabled) return;
    if (!isAdminUser) return;
    setReservationsHistoryLoading(true);
    try {
      const from = `${reservationsHistoryFromDate}T00:00`;
      const to = `${reservationsHistoryToDate}T23:59`;
      const list = await posApi.listReservations({
        scope: "history",
        from,
        to,
        limit: 500,
        status:
          reservationsHistoryStatus === "all"
            ? undefined
            : reservationsHistoryStatus,
      });
      setReservationsHistoryData(list);
    } catch (error) {
      reportApiError(error);
    } finally {
      setReservationsHistoryLoading(false);
    }
  }, [
    apiEnabled,
    isAdminUser,
    reportApiError,
    reservationsHistoryFromDate,
    reservationsHistoryStatus,
    reservationsHistoryToDate,
  ]);

  const getUpcomingReservationForTable = useCallback(
    (tableId: string): Reservation | null => {
      const upcoming = reservationsData
        .filter((r) => r.status === "scheduled" && r.tableId === tableId)
        .sort((a, b) => toMs(a.reservedAt) - toMs(b.reservedAt));
      return upcoming[0] ?? null;
    },
    [reservationsData],
  );

  const getReservationById = useCallback(
    (reservationId: string | null): Reservation | null => {
      if (!reservationId) return null;
      return (
        reservationsData.find((r) => r.id === reservationId) ??
        reservationsHistoryData.find((r) => r.id === reservationId) ??
        null
      );
    },
    [reservationsData, reservationsHistoryData],
  );

  const runMutation = useCallback(
    async (mutation: QueuedMutation, promise: Promise<unknown>) => {
      if (!apiEnabled) return;
      try {
        await promise;
        setApiOnline(true);
      } catch (error) {
        const { status } = getApiErrorInfo(error);
        if (status === 404) {
          setApiOnline(true);
          void refreshFromApi();
          return;
        }
        reportApiError(error);
        if (status === null) enqueueMutation(mutation);
      }
    },
    [
      apiEnabled,
      enqueueMutation,
      getApiErrorInfo,
      refreshFromApi,
      reportApiError,
    ],
  );

  const executeQueuedMutation = useCallback(
    async (mutation: QueuedMutation) => {
      if (!apiEnabled) return;
      switch (mutation.kind) {
        case "createTable":
          await posApi.createTable(mutation.payload);
          return;
        case "updateTable":
          if (!mutation.tableId) {
            console.warn("Skipping updateTable mutation because tableId is empty", mutation);
            return;
          }
          await posApi.updateTable(mutation.tableId, mutation.payload);
          return;
        case "deleteTable":
          if (!mutation.tableId) {
            console.warn("Skipping deleteTable mutation because tableId is empty", mutation);
            return;
          }
          await posApi.deleteTable(mutation.tableId);
          return;
        case "updateOrder":
          if (!mutation.orderId) {
            console.warn("Skipping updateOrder mutation because orderId is empty", mutation);
            return;
          }
          await posApi.updateOrder(mutation.orderId, mutation.payload);
          return;
        case "createOrder":
          await posApi.createOrder(mutation.payload);
          return;
        case "createPayment":
          await posApi.createPayment(mutation.payload);
          return;
        case "updateProduct":
          if (!mutation.productId) {
            console.warn("Skipping updateProduct mutation because productId is empty", mutation);
            return;
          }
          await posApi.updateProduct(mutation.productId, mutation.payload);
          return;
        default:
          return;
      }
    },
    [apiEnabled],
  );

  const derivedZones = useMemo(() => {
    const fromApi = zonesData.map((z) => z.name).filter(Boolean);
    if (fromApi.length > 0) return fromApi;
    const fromTables = Array.from(
      new Set(tablesData.map((t) => t.zone).filter(Boolean)),
    );
    return fromTables.length > 0 ? fromTables : ["General"];
  }, [tablesData, zonesData]);

  const availableTablesByZone = useMemo(() => {
    // Incluir TODAS las mesas para que se puedan seleccionar mesas ocupadas, libres o en cuenta
    const available = tablesData;
    const zoneMap = new Map<string, Table[]>();
    for (const t of available) {
      const zName = t.zone?.trim() || "Sin Zona";
      if (!zoneMap.has(zName)) {
        zoneMap.set(zName, []);
      }
      zoneMap.get(zName)!.push(t);
    }
    const orderedZoneNames: string[] = [];
    if (derivedZones && derivedZones.length > 0) {
      derivedZones.forEach((z) => {
        if (z && zoneMap.has(z)) orderedZoneNames.push(z);
      });
    }
    zoneMap.forEach((_, z) => {
      if (!orderedZoneNames.includes(z)) orderedZoneNames.push(z);
    });

    return orderedZoneNames.map((zoneName) => ({
      zone: zoneName,
      tables: zoneMap.get(zoneName)!.sort((a, b) => a.number - b.number),
    }));
  }, [tablesData, derivedZones]);

  const selectedTable = useMemo(
    () =>
      selectedTableId
        ? (tablesData.find((t) => t.id === selectedTableId) ?? null)
        : null,
    [selectedTableId, tablesData],
  );

  const selectedTakeawayOrder = useMemo(
    () =>
      selectedTakeawayOrderId
        ? ordersData.find(
            (o) => o.id === selectedTakeawayOrderId && !o.reservationId
          ) ?? null
        : null,
    [selectedTakeawayOrderId, ordersData],
  );

  const activeTakeawayOrders = useMemo(() => {
    return ordersData.filter(
      (o) => !o.tableId && !o.reservationId && o.status !== "closed" && o.status !== "paid",
    );
  }, [ordersData]);

  const reservationForActionTable = useMemo(() => {
    if (!reservationActionCandidate) return null;
    return getUpcomingReservationForTable(reservationActionCandidate.id);
  }, [getUpcomingReservationForTable, reservationActionCandidate]);

  useEffect(() => {
    if (!apiEnabled) return;
    void refreshCoreFromApi();
    void refreshAuxFromApi();
  }, [apiEnabled]);

  const corePollMs = useMemo(() => {
    switch (currentUserSafe.role) {
      case "admin":
        return 5_000;
      case "kitchen":
        return 10_000;
      case "cashier":
        return 10_000;
      case "waiter":
      default:
        return 3_000;
    }
  }, [currentUserSafe.role]);

  useEffect(() => {
    if (!apiEnabled) return;
    const interval = apiOnline ? corePollMs : 15_000;
    const timer = setInterval(() => {
      console.log("get cada x tiempo");
      void refreshCoreFromApi();
    }, interval);
    return () => clearInterval(timer);
  }, [apiEnabled, apiOnline, corePollMs]);

  useEffect(() => {
    if (!apiEnabled) return;
    if (!apiOnline) return;
    const timer = setInterval(() => {
      void refreshAuxFromApi();
    }, 30_000);
    return () => clearInterval(timer);
  }, [apiEnabled, apiOnline]);

  useEffect(() => {
    if (!apiEnabled) return;
    if (activeView !== "admin") return;
    void refreshAuxFromApi();
  }, [activeView, apiEnabled]);

  useEffect(() => {
    if (!apiEnabled) return;
    if (!apiOnline) return;
    if (activeView !== "tables") return;
    if (!isAdminUser) return;
    const timer = setInterval(() => {
      void refreshReservationsFromApi();
    }, 15_000);
    return () => clearInterval(timer);
  }, [activeView, apiEnabled, apiOnline, isAdminUser]);

  useEffect(() => {
    if (!apiEnabled) return;
    if (apiOnline) return;

    const timer = setInterval(() => {
      void refreshCoreFromApi();
    }, 5_000);
    return () => clearInterval(timer);
  }, [apiEnabled, apiOnline]);

  const allowedViews: View[] = (() => {
    switch (currentUserSafe.role) {
      case "waiter":
        return ["tables"];
      case "kitchen":
        return ["kitchen", "bar"];
      case "cashier":
        return ["cashier"];
      case "admin":
        return ["tables", "kitchen", "bar", "cashier", "admin"];
      default:
        return ["tables"];
    }
  })();

  const flushOfflineQueue = useCallback(async () => {
    if (!apiEnabled) return;
    if (flushInFlightRef.current) return;
    if (!apiOnline) return;
    if (offlineQueue.length === 0) return;

    flushInFlightRef.current = true;
    try {
      let hadSuccess = false;
      for (const mutation of offlineQueue) {
        try {
          await executeQueuedMutation(mutation);
          hadSuccess = true;
          setOfflineQueue((prev) => prev.filter((m) => m.id !== mutation.id));
          setApiOnline(true);
        } catch (error) {
          const { status } = getApiErrorInfo(error);
          if (status === 404) {
            setOfflineQueue((prev) => prev.filter((m) => m.id !== mutation.id));
            continue;
          }
          reportApiError(error);
          if (status === null || status === 401) break;
          setOfflineQueue((prev) => prev.filter((m) => m.id !== mutation.id));
        }
      }
      if (hadSuccess) {
        toast.success("Acciones sincronizadas", {
          description: "Cambios enviados a la API",
        });
        void refreshCoreFromApi();
        void refreshAuxFromApi();
      }
    } finally {
      flushInFlightRef.current = false;
    }
  }, [
    apiEnabled,
    apiOnline,
    executeQueuedMutation,
    getApiErrorInfo,
    offlineQueue,
    reportApiError,
    setOfflineQueue,
  ]);

  useEffect(() => {
    if (!apiEnabled) return;
    void flushOfflineQueue();
  }, [apiEnabled, flushOfflineQueue]);

  useEffect(() => {
    if (!apiEnabled) return;
    if (!apiOnline) return;
    if (offlineQueue.length === 0) return;
    const timer = setInterval(() => {
      void flushOfflineQueue();
    }, 5_000);
    return () => clearInterval(timer);
  }, [apiEnabled, apiOnline, flushOfflineQueue, offlineQueue.length]);

  useEffect(() => {
    if (!apiEnabled) return;
    if (apiOnline) {
      offlineWarnedIdsRef.current.clear();
      return;
    }
    const timer = setInterval(() => {
      const now = Date.now();
      for (const mutation of offlineQueue) {
        if (offlineWarnedIdsRef.current.has(mutation.id)) continue;
        const createdAt = Date.parse(mutation.createdAt);
        if (!Number.isFinite(createdAt)) continue;
        if (now - createdAt < 30_000) continue;
        offlineWarnedIdsRef.current.add(mutation.id);
        const isKitchenSend =
          mutation.kind === "updateOrder" &&
          typeof mutation.payload === "object" &&
          mutation.payload !== null &&
          "status" in mutation.payload &&
          (mutation.payload as Record<string, unknown>).status === "in-kitchen";
        toast.warning("Pendiente de sincronización", {
          description: isKitchenSend
            ? "La comanda sigue sin enviarse a cocina (30s). Puedes esperar o imprimir manualmente."
            : "Hay acciones sin sincronizar (30s). El sistema reintentará automáticamente.",
        });
      }
    }, 5_000);
    return () => clearInterval(timer);
  }, [apiEnabled, apiOnline, offlineQueue]);

  useEffect(() => {
    setSelectedTableId(null);
    setSelectedReservationId(null);
  }, [activeView]);

  const handleViewChange = (nextView: View) => {
    if (!allowedViews.includes(nextView)) {
      toast.error("No autorizado");
      return;
    }
    onViewChange?.(nextView);
    if (!isControlled) {
      setActiveViewInternal(nextView);
    }
  };

  const upsertOrderForTable = useCallback(
    (
      table: Table | null,
      items: OrderItem[],
      status: Order["status"],
      options?: { updateTable?: boolean; reservationId?: string | null; send_to_kitchen?: boolean; immediate?: boolean; takeawayOrderId?: string | null },
    ) => {
      const shouldUpdateTable = options?.updateTable !== false;
      const seatedRes = table
        ? reservationsData.find(
            (r) => r.tableId === table.id && r.status === "seated",
          )
        : null;
      const reservationId =
        options?.reservationId ??
        (table?.currentOrderId
          ? (ordersData.find((o) => o.id === table.currentOrderId)?.reservationId ?? null)
          : null) ??
        seatedRes?.id ??
        null;
      const isPreOrder = Boolean(reservationId) || !shouldUpdateTable;
      const isTakeaway = table === null;
      const targetTakeawayId = options?.takeawayOrderId ?? selectedTakeawayOrderId ?? selectedTakeawayOrder?.id;

      const activeExisting = reservationId
  ? (ordersData.find(
      (o) =>
        o.reservationId === reservationId &&
        o.status !== "paid" &&
        o.status !== "closed",
    ) ?? null)
  : isTakeaway
    ? (targetTakeawayId
        ? (ordersData.find(
            (o) =>
              o.id === targetTakeawayId &&
              !o.tableId &&
              !o.reservationId &&
              o.status !== "paid" &&
              o.status !== "closed",
          ) ?? null)
        : selectedTakeawayOrder)
    : ((table?.currentOrderId
        ? (ordersData.find(
            (o) =>
              o.id === table.currentOrderId &&
              o.status !== "paid" &&
              o.status !== "closed",
          ) ?? null)
        : null) ??
      ordersData.find(
        (o) =>
          o.tableId === table?.id &&
          !o.reservationId &&
          o.status !== "paid" &&
          o.status !== "closed",
      ) ??
      null);

      if (items.length === 0 && !activeExisting) return;

      const now = new Date();
      const { subtotal, tax, total } = computeTotals(items);
      const orderId = activeExisting?.id ?? createId("ord");
      const computedClientName = reservationId
        ? (getReservationById(reservationId)?.reserverName ?? undefined)
        : (activeExisting?.clientName ?? selectedTakeawayOrder?.clientName);

      const existingForPending = activeExisting;
      if (existingForPending) {
        const pendingOrder: Order = {
          ...existingForPending,
          items,
          status,
          updatedAt: now,
          sentToKitchenAt:
            status === "in-kitchen"
              ? (existingForPending.sentToKitchenAt ?? now)
              : existingForPending.sentToKitchenAt,
          subtotal,
          tax,
          total,
        };
        pendingOrdersRef.current.set(existingForPending.id, {
          order: pendingOrder,
          updatedAtMs: now.getTime(),
        });
      } else {
        const pendingOrder: Order = {
          id: orderId,
          tableId: table?.id,
          tableNumber: table?.number,
          clientName: computedClientName,
          waiter: currentUserSafe.name,
          items,
          status,
          createdAt: now,
          updatedAt: now,
          sentToKitchenAt: status === "in-kitchen" ? now : undefined,
          subtotal,
          tax,
          total,
        };
        pendingOrdersRef.current.set(orderId, {
          order: pendingOrder,
          updatedAtMs: now.getTime(),
        });
      }

      const existing = reservationId
  ? ordersData.find(
      (o) =>
        o.reservationId === reservationId &&
        o.status !== "paid" &&
        o.status !== "closed",
    )
  : isTakeaway
    ? (targetTakeawayId
        ? (ordersData.find(
            (o) =>
              o.id === targetTakeawayId &&
              !o.tableId &&
              !o.reservationId &&
              o.status !== "paid" &&
              o.status !== "closed",
          ) ?? null)
        : selectedTakeawayOrder)
    : ordersData.find(
        (o) =>
          o.tableId === table?.id &&
          (table?.currentOrderId ? o.id === table.currentOrderId : !o.reservationId) &&
          o.status !== "paid" &&
          o.status !== "closed",
      );

      // Actualización optimista inmediata en estado local (0ms lag para la UI)
      if (existing) {
        setOrdersData((prev) =>
          prev.map((o) =>
            o.id === existing.id
              ? {
                  ...o,
                  items,
                  status,
                  updatedAt: now,
                  sentToKitchenAt:
                    status === "in-kitchen"
                      ? (o.sentToKitchenAt ?? now)
                      : o.sentToKitchenAt,
                  subtotal,
                  tax,
                  total,
                }
              : o,
          ),
        );
      } else {
        const newOrder: Order = {
          id: orderId,
          tableId: table?.id,
          reservationId: reservationId || undefined,
          tableNumber: table?.number,
          clientName: computedClientName,
          waiter: currentUserSafe.name,
          items,
          status,
          createdAt: now,
          updatedAt: now,
          sentToKitchenAt: status === "in-kitchen" ? now : undefined,
          subtotal,
          tax,
          total,
        };

        setOrdersData((prev) => {
          const exists = prev.find((o) => o.id === orderId);
          if (exists) {
            return prev.map((o) => (o.id === orderId ? newOrder : o));
          }
          return [newOrder, ...prev];
        });
      }

      if (shouldUpdateTable && table) {
        setTablesData((prev) => {
          const next = prev.map((t) => {
            if (t.id !== table.id) return t;
            const openedAt = t.openedAt ?? now;
            return {
              ...t,
              status: "occupied" as const,
              currentOrderId: t.currentOrderId ?? orderId,
              waiter: currentUserSafe.name,
              openedAt,
            };
          });
          return next;
        });
      }

      // Batching/Debounce de red (400ms) para ráfagas rápidas de clics al agregar platos
      const timerKey = reservationId
        ? `res-${reservationId}`
        : isTakeaway
        ? `takeaway-${activeExisting?.id ?? "new"}`
        : `table-${table?.id ?? "none"}`;

      const existingTimer = debouncedDraftTimersRef.current.get(timerKey);
      if (existingTimer) {
        clearTimeout(existingTimer);
        debouncedDraftTimersRef.current.delete(timerKey);
      }

      const executeApiCall = () => {
        debouncedDraftTimersRef.current.delete(timerKey);

        const isPersisted = existing
          ? (persistedOrderIdsRef.current.has(existing.id) || !existing.id.startsWith("ord-"))
          : false;

        if (isPersisted && existing) {
          const updatePayload = {
            items,
            status,
            subtotal,
            tax,
            total,
            client_name: computedClientName ?? null,
            is_pre_order: isPreOrder,
            send_to_kitchen: options?.send_to_kitchen,
          };
          const promise = posApi.updateOrder(existing.id, updatePayload).then((savedOrder) => {
            persistedOrderIdsRef.current.add(savedOrder.id);
            setOrdersData((prev) =>
              prev.map((o) => (o.id === savedOrder.id ? savedOrder : o)),
            );
            return savedOrder;
          });

          void runMutation(
            {
              id: createId("q"),
              kind: "updateOrder",
              orderId: existing.id,
              payload: updatePayload,
              createdAt: now.toISOString(),
            },
            promise,
          );
        } else {
          const orderPayload = {
            id: orderId,
            tableId: table?.id ?? null,
            reservationId: reservationId || undefined,
            tableNumber: table?.number ?? null,
            client_name: computedClientName ?? null,
            waiter: currentUserSafe.name,
            items,
            status,
            subtotal,
            tax,
            total,
            is_pre_order: isPreOrder,
            send_to_kitchen: options?.send_to_kitchen,
          };

          const promise = posApi.createOrder(orderPayload).then((savedOrder) => {
            persistedOrderIdsRef.current.add(savedOrder.id);
            setOrdersData((prev) =>
              prev.map((o) => (o.id === savedOrder.id ? savedOrder : o)),
            );
            return savedOrder;
          });

          void runMutation(
            {
              id: createId("q"),
              kind: "createOrder",
              payload: orderPayload,
              createdAt: now.toISOString(),
            },
            promise,
          );
        }

        if (shouldUpdateTable && table) {
          const tablePayload = {
            status: "occupied",
            currentOrderId: orderId,
            waiter: currentUserSafe.name,
            openedAt: now.toISOString(),
          };

          void runMutation(
            {
              id: createId("q"),
              kind: "updateTable",
              tableId: table.id,
              payload: tablePayload,
              createdAt: now.toISOString(),
            },
            posApi.updateTable(table.id, tablePayload),
          );
        }
      };

      if (options?.immediate || options?.send_to_kitchen) {
        executeApiCall();
      } else {
        const timer = setTimeout(executeApiCall, 400);
        debouncedDraftTimersRef.current.set(timerKey, timer);
      }
    },
    [computeTotals, createId, currentUserSafe.name, getReservationById, ordersData, reservationsData, runMutation, selectedTakeawayOrder, selectedTakeawayOrderId],
  );

  const confirmOpenTable = async (table: Table, dinersText: string) => {
    const latest = tablesData.find((t) => t.id === table.id);
    const bypassUntil = reservationUnlockBypassRef.current.get(table.id) ?? 0;
    const canBypass = bypassUntil > Date.now();
    const allowed =
      Boolean(latest) &&
      (latest?.status === "available" ||
        (latest?.status === "blocked" && canBypass));
    if (!allowed) {
      toast.error("La mesa ya no está disponible");
      void refreshFromApi();
      return;
    }

    const dinersNumber = dinersText.trim() ? Number(dinersText) : NaN;
    const diners =
      Number.isFinite(dinersNumber) && dinersNumber > 0
        ? Math.floor(dinersNumber)
        : undefined;
    const now = new Date();

    const upcomingRes = getUpcomingReservationForTable(table.id);

    let shouldLinkReservation = false;
    if (upcomingRes) {
      const timeDiffMs = toMs(upcomingRes.reservedAt) - Date.now();
      if (canBypass) {
        shouldLinkReservation = true;
      } else if (timeDiffMs <= 0 && timeDiffMs >= -45 * 60 * 1000) {
        shouldLinkReservation = true;
      }
    }

    const linkedRes = shouldLinkReservation ? upcomingRes : null;
    const reservationOrder = linkedRes
      ? ordersData.find(
          (o) =>
            o.reservationId === linkedRes.id &&
            o.status !== "paid" &&
            o.status !== "closed",
        )
      : null;

    const orderId = reservationOrder ? reservationOrder.id : createId("ord");
    const items = reservationOrder ? reservationOrder.items : [];
    const { subtotal, tax, total } = computeTotals(items);

    if (linkedRes) {
      void posApi
        .updateReservation(linkedRes.id, { status: "seated" })
        .then((updatedRes) => {
          setReservationsData((prev) =>
            prev.map((r) => (r.id === updatedRes.id ? updatedRes : r)),
          );
        })
        .catch(console.error);
    }

    const newOrder: Order = {
      id: orderId,
      tableId: table.id,
      tableNumber: table.number,
      waiter: currentUserSafe.name,
      diners: diners ?? reservationOrder?.diners,
      items,
      status: reservationOrder ? reservationOrder.status : "open",
      createdAt: reservationOrder ? reservationOrder.createdAt : now,
      updatedAt: now,
      subtotal,
      tax,
      total,
      reservationId: linkedRes?.id || undefined,
    };

    pendingOrdersRef.current.set(orderId, {
      order: newOrder,
      updatedAtMs: now.getTime(),
    });

    const hasActiveOtherOrder = ordersData.some(
      (o) =>
        o.tableId === table.id &&
        o.status !== "closed" &&
        o.status !== "paid" &&
        o.id !== orderId &&
        (!o.reservationId || o.id === table.currentOrderId || (linkedRes && o.reservationId === linkedRes.id)),
    );

    if (!hasActiveOtherOrder) {
      const payload = {
        id: newOrder.id,
        tableId: newOrder.tableId,
        tableNumber: newOrder.tableNumber,
        reservationId: newOrder.reservationId,
        reservation_id: newOrder.reservationId,
        waiter: newOrder.waiter,
        diners: newOrder.diners,
        items: newOrder.items,
        status: newOrder.status,
        subtotal: newOrder.subtotal,
        tax: newOrder.tax,
        total: newOrder.total,
        is_pre_order: false,
      };

      try {
        if (apiEnabled) {
          const savedOrder = await posApi.createOrder(payload);
          setOrdersData((prev) => {
            const exists = prev.find((o) => o.id === savedOrder.id);
            if (exists) {
              return prev.map((o) => (o.id === savedOrder.id ? savedOrder : o));
            }
            return [savedOrder, ...prev];
          });
        } else {
          setOrdersData((prev) => {
            const exists = prev.find((o) => o.id === orderId);
            if (exists) {
              return prev.map((o) => (o.id === orderId ? newOrder : o));
            }
            return [newOrder, ...prev];
          });
        }
      } catch (error) {
        pendingOrdersRef.current.delete(orderId);
        reportApiError(error);
        setOpenTableCandidate(null);
        setOpenTableDinersText("");
        void refreshFromApi();
        return;
      }
    }

    setTablesData((prev) => {
      const next = prev.map((t) => {
        if (t.id !== table.id) return t;
        return {
          ...t,
          status: "occupied" as const,
          currentOrderId: orderId,
          waiter: currentUserSafe.name,
          openedAt: now,
        };
      });
      const tablePayload = {
        status: "occupied",
        currentOrderId: orderId,
        waiter: currentUserSafe.name,
        openedAt: now.toISOString(),
      };
      void runMutation(
        {
          id: createId("q"),
          kind: "updateTable",
          tableId: table.id,
          payload: tablePayload,
          createdAt: now.toISOString(),
        },
        posApi.updateTable(table.id, tablePayload),
      );
      return next;
    });

    appendActivity({
      actor: currentUserSafe.name,
      actorRole: currentUserSafe.role,
      entityType: "table",
      entityId: table.id,
      fromStatus: "available",
      toStatus: "occupied",
      action: "table_opened",
      tableNumber: table.number,
      details: diners
        ? `Mesa ${table.number} abierta (${diners} comensales)`
        : `Mesa ${table.number} abierta`,
    });

    setSelectedTableId(table.id);
    setSelectedReservationId(null);
    setSelectedTakeawayOrderId(null);
    setOpenTableCandidate(null);
    setOpenTableDinersText("");
  };

  const handleTransferTableConfirm = async () => {
    if (!transferOriginTable || !transferDestinationTableId) {
      toast.error("Debe seleccionar una mesa de origen y una mesa de destino.");
      return;
    }
    setIsTransferring(true);
    try {
      await posApi.transferTable(transferOriginTable.id, transferDestinationTableId);
      toast.success("Mesa trasladada exitosamente.");
      
      setTransferOriginTable(null);
      setTransferDestinationTableId("");
      setSelectedTableId(null);
      setSelectedTakeawayOrderId(null);
      setSelectedReservationId(null);
      
      await refreshFromApi();
    } catch (err: any) {
      console.error(err);
      const errMsg = err?.response?.data?.message || err?.message || "Error al trasladar la mesa";
      toast.error(errMsg);
    } finally {
      setIsTransferring(false);
    }
  };

  const handleDeleteOrder = async (orderId: string) => {
    try {
      const orderToDelete = ordersData.find((o) => o.id === orderId);
      await posApi.deleteOrder(orderId);

      pendingOrdersRef.current.delete(orderId);

      setOrdersData((prev) => prev.filter((o) => o.id !== orderId));

      setTablesData((prev) =>
        prev.map((t) => {
          if (
            t.currentOrderId === orderId ||
            (orderToDelete?.tableId && t.id === orderToDelete.tableId)
          ) {
            return {
              ...t,
              status: "available" as const,
              currentOrderId: undefined,
              waiter: undefined,
              openedAt: undefined,
            };
          }
          return t;
        })
      );

      toast.success("Comanda anulada y eliminada exitosamente.");

      setSelectedTableId(null);
      setSelectedTakeawayOrderId(null);
      setSelectedReservationId(null);

      await refreshFromApi();
    } catch (err: any) {
      console.error(err);
      const errMsg = err?.response?.data?.message || err?.message || "Error al eliminar la comanda";
      toast.error(errMsg);
    }
  };


   const confirmTakeAway = (clientName: string) => {
    const now = new Date();
    const orderId = createId("ord");
    pendingOrdersRef.current.set(orderId, {
      order: {
        id: orderId,
        tableId: undefined,
        tableNumber: undefined,
        clientName: clientName,
        waiter: currentUserSafe.name,
        diners: undefined,
        items: [],
        status: "open",
        createdAt: now,
        updatedAt: now,
        subtotal: 0,
        tax: 0,
        total: 0,
      },
      updatedAtMs: now.getTime(),
    });

    const newOrder: Order = {
      id: orderId,
      tableId: undefined,
      tableNumber: undefined,
      clientName: clientName,
      waiter: currentUserSafe.name,
      diners: undefined,
      items: [],
      status: "open",
      createdAt: now,
      updatedAt: now,
      subtotal: 0,
      tax: 0,
      total: 0,
    };

    const payload = {
      id: newOrder.id,
      tableId: newOrder.tableId,
      tableNumber: newOrder.tableNumber,
      client_name: newOrder.clientName,
      waiter: newOrder.waiter,
      diners: newOrder.diners,
      items: newOrder.items,
      status: newOrder.status,
      subtotal: newOrder.subtotal,
      tax: newOrder.tax,
      total: newOrder.total,
    };

    const promise = posApi.createOrder(payload).then((savedOrder) => {
      setOrdersData((prev) =>
        prev.map((o) => (o.id === savedOrder.id ? savedOrder : o)),
      );
      return savedOrder;
    });

    void runMutation(
      {
        id: createId("q"),
        kind: "createOrder",
        payload,
        createdAt: now.toISOString(),
      },
      promise,
    );

    setOrdersData((prev) => [newOrder, ...prev]);

    appendActivity({
      actor: currentUserSafe.name,
      actorRole: currentUserSafe.role,
      entityType: "table",
      fromStatus: "available",
      toStatus: "occupied",
      action: "table_opened",
      details: "Order creada para cliente " + clientName,  
    });


    setSelectedTakeawayOrderId(orderId);
    setSelectedTableId(null);
    setSelectedReservationId(null);
    setOpenTableCandidate(null);
    setOpenTableDinersText("");
    setOpenTakeawayClient(false);
  };

  const startTablelessReservation = useCallback(() => {
    setReserveTableCandidate(null);
    setReservationIsTableless(true);
    setIsReservationModalOpen(true);
    setReservationReserverName("");
    setReservationReserverPhone("");
    setReservationReserverEmail("");
    setReservationPartySizeText("2");
    setReservationReservedAtText(
      toDateTimeLocalValue(new Date(Date.now() + 30 * 60 * 1000)),
    );
    setReservationPreBlockMinutesText("15");
    setReservationUnlockCodeText(generateUnlockCode());
    setReservationKitchenLeadMinutesText("0");
    setReservationSendMode("auto");
    setReservationPrintTicketTiming("on_assign_or_due");
    setReservationNotes("");
  }, [generateUnlockCode, toDateTimeLocalValue]);

  const startReservationForTable = useCallback(
    (table: Table) => {
      setReserveTableCandidate(table);
      setReservationIsTableless(false);
      setIsReservationModalOpen(true);
      setReservationReserverName("");
      setReservationReserverPhone("");
      setReservationReserverEmail("");
      setReservationPartySizeText(
        String(Math.max(1, Math.min(table.capacity || 1, 12))),
      );
      setReservationReservedAtText(
        toDateTimeLocalValue(new Date(Date.now() + 30 * 60 * 1000)),
      );
      setReservationPreBlockMinutesText("15");
      setReservationUnlockCodeText(generateUnlockCode());
      setReservationKitchenLeadMinutesText("0");
      setReservationSendMode("auto");
      setReservationPrintTicketTiming("on_assign_or_due");
      setReservationNotes("");
    },
    [generateUnlockCode, toDateTimeLocalValue],
  );

  const confirmCreateReservation = useCallback(async () => {
    if (!apiEnabled) return;
    if (reservationSubmitting) return;

    const reserverName = reservationReserverName.trim();
    if (!reserverName) {
      toast.error("Falta el nombre de quien reserva");
      return;
    }

    const partySizeNumber = Number(reservationPartySizeText);
    const partySize = Number.isFinite(partySizeNumber)
      ? Math.floor(partySizeNumber)
      : 0;
    if (partySize < 1) {
      toast.error("Cantidad de comensales inválida");
      return;
    }

    const reservedAt = reservationReservedAtText.trim();
    if (!reservedAt) {
      toast.error("Falta la fecha/hora de la reserva");
      return;
    }

    const preBlockNumber = reservationPreBlockMinutesText.trim()
      ? Number(reservationPreBlockMinutesText)
      : NaN;
    const preBlockMinutes = Number.isFinite(preBlockNumber)
      ? Math.max(0, Math.floor(preBlockNumber))
      : NaN;
    if (!Number.isFinite(preBlockMinutes) || preBlockMinutes > 1440) {
      toast.error("Minutos de bloqueo previo inválidos");
      return;
    }

    const unlockCode = reservationUnlockCodeText.trim();
    if (unlockCode.length < 4) {
      toast.error("Código de desbloqueo inválido");
      return;
    }

    const leadNumber = reservationKitchenLeadMinutesText.trim()
      ? Number(reservationKitchenLeadMinutesText)
      : NaN;
    const kitchenLeadMinutes = Number.isFinite(leadNumber)
      ? Math.max(0, Math.floor(leadNumber))
      : NaN;
    if (!Number.isFinite(kitchenLeadMinutes) || kitchenLeadMinutes > 1440) {
      toast.error("Tiempo estimado de cocina inválido");
      return;
    }

    setReservationSubmitting(true);
    try {
      const created = await posApi.createReservation({
        tableId: reservationIsTableless ? null : (reserveTableCandidate?.id ?? null),
        reserverName,
        reserverPhone: reservationReserverPhone.trim()
          ? reservationReserverPhone.trim()
          : undefined,
        reserverEmail: reservationReserverEmail.trim()
          ? reservationReserverEmail.trim()
          : undefined,
        partySize,
        notes: reservationNotes.trim() ? reservationNotes.trim() : undefined,
        reservedAt,
        preBlockMinutes,
        unlockCode,
        kitchenLeadMinutes,
        sendMode: reservationSendMode,
        printTicketTiming: reservationPrintTicketTiming,
      });
      toast.success("Reserva creada", { description: `Código: ${unlockCode}` });
      setReservationsData((prev) => {
        const next = prev.filter((r) => r.id !== created.id);
        return [created, ...next];
      });
      setSelectedTableId(created.tableId ?? null);
      setSelectedReservationId(created.id);
      setSelectedTakeawayOrderId(null);
      setReserveTableCandidate(null);
      setIsReservationModalOpen(false);
      void refreshReservationsFromApi();
    } catch (error) {
      reportApiError(error);
    } finally {
      setReservationSubmitting(false);
    }
  }, [
    apiEnabled,
    refreshReservationsFromApi,
    reportApiError,
    reservationNotes,
    reservationPartySizeText,
    reservationReserverEmail,
    reservationReserverName,
    reservationReserverPhone,
    reservationPreBlockMinutesText,
    reservationReservedAtText,
    reservationUnlockCodeText,
    reservationKitchenLeadMinutesText,
    reservationSendMode,
    reservationPrintTicketTiming,
    reservationIsTableless,
    reserveTableCandidate,
    reservationSubmitting,
  ]);

  const confirmUnlockReservedTable = useCallback(async () => {
    if (!apiEnabled) return;
    if (!unlockReservedTableCandidate) return;
    const code = unlockReservedTableCodeText.trim();
    if (code.length < 4) {
      toast.error("Código inválido");
      return;
    }
    try {
      try {
        await posApi.reservationUnlockTable(
          unlockReservedTableCandidate.id,
          code,
        );
      } catch {
        await posApi.unlockTable(unlockReservedTableCandidate.id, code);
      }
      reservationUnlockBypassRef.current.set(
        unlockReservedTableCandidate.id,
        Date.now() + 60_000,
      );
      setTablesData((prev) =>
        prev.map((t) =>
          t.id === unlockReservedTableCandidate.id
            ? { ...t, status: "available" as const, blockedReason: undefined }
            : t,
        ),
      );
      setUnlockReservedTableCandidate(null);
      setUnlockReservedTableCodeText("");
      setOpenTableCandidate({
        ...unlockReservedTableCandidate,
        status: "available" as const,
        blockedReason: undefined,
      });
      setOpenTableDinersText("");
    } catch {
      toast.error("Código inválido");
    }
  }, [apiEnabled, unlockReservedTableCandidate, unlockReservedTableCodeText]);

  const startEditReservation = useCallback(
    (reservation: Reservation) => {
      setEditReservationCandidate(reservation);
      setEditReservationReserverName(reservation.reserverName ?? "");
      setEditReservationReserverPhone(reservation.reserverPhone ?? "");
      setEditReservationReserverEmail(reservation.reserverEmail ?? "");
      setEditReservationPartySizeText(String(reservation.partySize ?? 1));
      setEditReservationReservedAtText(
        toDateTimeLocalValue(reservation.reservedAt),
      );
      setEditReservationPreBlockMinutesText(
        String(reservation.preBlockMinutes ?? 15),
      );
      setEditReservationKitchenLeadMinutesText(
        String(reservation.kitchenLeadMinutes ?? 0),
      );
      setEditReservationSendMode(reservation.sendMode ?? "auto");
      setEditReservationPrintTicketTiming(reservation.printTicketTiming ?? "on_assign_or_due");
      setEditReservationNotes(reservation.notes ?? "");
    },
    [toDateTimeLocalValue],
  );

  const confirmUpdateReservation = useCallback(async () => {
    if (!apiEnabled) return;
    if (!editReservationCandidate) return;
    if (reservationSubmitting) return;

    const reserverName = editReservationReserverName.trim();
    if (!reserverName) {
      toast.error("Falta el nombre de quien reserva");
      return;
    }

    const partySizeNumber = Number(editReservationPartySizeText);
    const partySize = Number.isFinite(partySizeNumber)
      ? Math.floor(partySizeNumber)
      : 0;
    if (partySize < 1) {
      toast.error("Cantidad de comensales inválida");
      return;
    }

    const reservedAt = editReservationReservedAtText.trim();
    if (!reservedAt) {
      toast.error("Falta la fecha/hora de la reserva");
      return;
    }

    const preBlockNumber = editReservationPreBlockMinutesText.trim()
      ? Number(editReservationPreBlockMinutesText)
      : NaN;
    const preBlockMinutes = Number.isFinite(preBlockNumber)
      ? Math.max(0, Math.floor(preBlockNumber))
      : NaN;
    if (!Number.isFinite(preBlockMinutes) || preBlockMinutes > 1440) {
      toast.error("Minutos de bloqueo previo inválidos");
      return;
    }

    const leadNumber = editReservationKitchenLeadMinutesText.trim()
      ? Number(editReservationKitchenLeadMinutesText)
      : NaN;
    const kitchenLeadMinutes = Number.isFinite(leadNumber)
      ? Math.max(0, Math.floor(leadNumber))
      : NaN;
    if (!Number.isFinite(kitchenLeadMinutes) || kitchenLeadMinutes > 1440) {
      toast.error("Tiempo estimado de cocina inválido");
      return;
    }

    setReservationSubmitting(true);
    try {
      await posApi.updateReservation(editReservationCandidate.id, {
        reserverName,
        reserverPhone: editReservationReserverPhone.trim()
          ? editReservationReserverPhone.trim()
          : null,
        reserverEmail: editReservationReserverEmail.trim()
          ? editReservationReserverEmail.trim()
          : null,
        partySize,
        notes: editReservationNotes.trim() ? editReservationNotes.trim() : null,
        reservedAt,
        preBlockMinutes,
        kitchenLeadMinutes,
        sendMode: editReservationSendMode,
        printTicketTiming: editReservationPrintTicketTiming,
      });
      toast.success("Reserva actualizada");
      setEditReservationCandidate(null);
      void refreshReservationsFromApi();
    } catch (error) {
      reportApiError(error);
    } finally {
      setReservationSubmitting(false);
    }
  }, [
    apiEnabled,
    editReservationCandidate,
    editReservationNotes,
    editReservationPartySizeText,
    editReservationPreBlockMinutesText,
    editReservationKitchenLeadMinutesText,
    editReservationSendMode,
    editReservationPrintTicketTiming,
    editReservationReservedAtText,
    editReservationReserverEmail,
    editReservationReserverName,
    editReservationReserverPhone,
    refreshReservationsFromApi,
    reportApiError,
    reservationSubmitting,
  ]);

  const confirmCancelReservation = useCallback(async () => {
    if (!apiEnabled) return;
    if (!cancelReservationCandidate) return;
    const cand = cancelReservationCandidate;
    try {
      await posApi.updateReservation(cand.id, {
        status: "cancelled",
      });
      setOrdersData((prev) =>
        prev.filter((o) => o.reservationId !== cand.id && o.id !== cand.orderId)
      );
      toast.success("Reserva anulada y comanda eliminada de cocina");
      setCancelReservationCandidate(null);
      void refreshReservationsFromApi();
      void refreshFromApi();
    } catch (error) {
      reportApiError(error);
    }
  }, [
    apiEnabled,
    cancelReservationCandidate,
    refreshReservationsFromApi,
    refreshFromApi,
    reportApiError,
  ]);

  const handleConfirmAssignReservationInPos = useCallback(
    async (table: Table) => {
      if (!assignTableReservationCandidate) return;
      const isOccupied = table.status === "occupied" || table.status === "pending-payment" || Boolean(table.currentOrderId);
      try {
        await posApi.updateReservation(
          assignTableReservationCandidate.id,
          { tableId: table.id, status: "seated" }
        );
        if (isOccupied) {
          toast.success(`Reserva sumada exitosamente a la Mesa ${table.number}`);
        } else {
          toast.success(`Reserva asignada a Mesa ${table.number} y abierta en salón`);
        }
        setAssignTableReservationCandidate(null);
        setConfirmOccupiedTableInPos(null);
        void refreshReservationsFromApi();
        void refreshFromApi();
      } catch (err) {
        reportApiError(err);
      }
    },
    [assignTableReservationCandidate, refreshFromApi, refreshReservationsFromApi, reportApiError]
  );

  const handleDownloadReservationsReport = useCallback(() => {
    const upcoming = reservationsData
      .filter((r) => r.status === "scheduled")
      .sort((a, b) => toMs(a.reservedAt) - toMs(b.reservedAt));

    if (upcoming.length === 0) {
      toast.info("No hay reservas próximas para descargar");
      return;
    }

    let report = "REPORTE DE PRÓXIMAS RESERVAS\n";
    report += "Generado el: " + new Date().toLocaleString() + "\n";
    report += "=".repeat(50) + "\n\n";

    upcoming.forEach((r) => {
      report += `Fecha y Hora: ${toMs(r.reservedAt) ? new Date(toMs(r.reservedAt)).toLocaleString() : ""}\n`;
      report += `Mesa: ${r.tableNumber} (${r.zone})\n`;
      report += `A nombre de: ${r.reserverName}\n`;
      report += `Comensales: ${r.partySize}\n`;

      const order = ordersData.find(
        (o) =>
          o.reservationId === r.id &&
          o.status !== "paid" &&
          o.status !== "closed",
      );

      if (order && order.items.length > 0) {
        report += `Comanda programada (${order.items.length} items):\n`;
        order.items.forEach((item) => {
          report += `  - ${item.quantity}x ${item.productName}\n`;
          if (item.notes) {
            report += `    Nota: ${item.notes}\n`;
          }
        });
      } else {
        report += `Comanda programada: Sin productos\n`;
      }
      report += "-".repeat(30) + "\n\n";
    });

    const blob = new Blob([report], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `reservas_${new Date().toISOString().slice(0, 10).replace(/-/g, "")}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [reservationsData, ordersData]);

  const handleDownloadReservationsHistoryExcel = useCallback(() => {
    if (reservationsHistoryData.length === 0) {
      toast.info("No hay datos de historial para descargar");
      return;
    }

    const escapeCell = (value: unknown) => {
      const str = value === null || value === undefined ? "" : String(value);
      const needsQuotes = /[";\n\r]/.test(str);
      const escaped = str.replace(/"/g, '""');
      return needsQuotes ? `"${escaped}"` : escaped;
    };

    const pad2 = (n: number) => String(n).padStart(2, "0");
    const formatDate = (d: Date) =>
      `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
    const formatTime = (d: Date) =>
      `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;

    const header = [
      "Fecha",
      "Hora",
      "Mesa",
      "Zona",
      "Nombre",
      "Teléfono",
      "Email",
      "Comensales",
      "Estado",
      "BloqueoPrevioMin",
      "CocinaMinAntes",
      "Items",
      "DetalleItems",
    ];

    const rows = reservationsHistoryData
      .slice()
      .sort((a, b) => toMs(a.reservedAt) - toMs(b.reservedAt))
      .map((r) => {
        const order =
          ordersData.find(
            (o) =>
              o.reservationId === r.id &&
              o.status !== "paid" &&
              o.status !== "closed",
          ) ?? null;
        const items = (order?.items ?? []).filter(
          (i) => i.status !== "cancelled",
        );
        const detail = items
          .map(
            (i) =>
              `${i.quantity}x ${i.productName}${i.notes ? ` (${i.notes})` : ""}`,
          )
          .join(" | ");
        return [
          formatDate(r.reservedAt),
          formatTime(r.reservedAt),
          r.tableNumber,
          r.zone,
          r.reserverName,
          r.reserverPhone ?? "",
          r.reserverEmail ?? "",
          r.partySize,
          r.status,
          r.preBlockMinutes,
          r.kitchenLeadMinutes,
          items.length,
          detail,
        ]
          .map(escapeCell)
          .join(";");
      });

    const bom = "\uFEFF";
    const csv = bom + [header.map(escapeCell).join(";"), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `historial_reservas_${reservationsHistoryFromDate.replace(/-/g, "")}_${reservationsHistoryToDate.replace(/-/g, "")}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [
    ordersData,
    reservationsHistoryData,
    reservationsHistoryFromDate,
    reservationsHistoryToDate,
  ]);

  const handleSaveOrder = (
    table: Table | null | undefined,
    items: OrderItem[],
  ) => {
    if (!table) {
      const nextStatus =
        selectedTakeawayOrder && selectedTakeawayOrder.status !== "open"
          ? selectedTakeawayOrder.status
          : "open";
      upsertOrderForTable(null, items, nextStatus, {
        updateTable: false,
        reservationId: selectedReservationId ?? null,
        takeawayOrderId: selectedTakeawayOrderId,
      });

      return;
    }

  const selectedReservation = selectedReservationId
    ? getReservationById(selectedReservationId)
    : null;

  const reservation =
    selectedReservation && selectedReservation.tableId === table.id
      ? selectedReservation
      : getUpcomingReservationForTable(table.id);

  const isReservationPreOrder =
    Boolean(reservation) && table.status !== "occupied";

  const hadOrder = ordersData.some((o) => o.tableId === table.id);

  const existingOrder = ordersData.find(
    (o) =>
      o.tableId === table.id &&
      (table.currentOrderId ? o.id === table.currentOrderId : !o.reservationId) &&
      o.status !== "paid" &&
      o.status !== "closed"
  );
  const nextStatus = existingOrder && existingOrder.status !== "open" ? existingOrder.status : "open";

  upsertOrderForTable(table, items, nextStatus, {
    updateTable: !isReservationPreOrder,
    reservationId: isReservationPreOrder ? reservation?.id : null,
  });

  if (
    isReservationPreOrder &&
    reservation &&
    reservation.sendMode === "auto" &&
    toMs(reservation.reservedAt) > Date.now() &&
    reservation.kitchenLeadMinutes > 0
  ) {
    const targetAtMs =
      toMs(reservation.reservedAt) -
      reservation.kitchenLeadMinutes * 60_000;

    const executeAtMs = Math.max(Date.now(), targetAtMs);

    const existingOrderId = ordersData.find(
      (o) =>
        o.reservationId === reservation.id &&
        o.status !== "paid" &&
        o.status !== "closed",
    )?.id;

    const scheduled: ScheduledKitchenSend = {
      id: createId("sched"),
      tableId: table.id,
      orderId: existingOrderId,
      reservationId: reservation.id,
      executeAt: new Date(executeAtMs).toISOString(),
      createdAt: new Date().toISOString(),
    };

    setScheduledKitchenSends((prev) => {
      const next = prev.filter((s) => s.reservationId !== reservation.id);
      return [...next, scheduled];
    });

    toast.success("Comanda programada para cocina", {
      description: new Date(executeAtMs).toLocaleString(),
    });
  }

  if (!isReservationPreOrder && !hadOrder && items.length > 0) {
    appendActivity({
      actor: currentUserSafe.name,
      actorRole: currentUserSafe.role,
      entityType: "table",
      entityId: table.id,
      fromStatus: "available",
      toStatus: "occupied",
      action: "table_opened",
      tableNumber: table.number,
      details: `Mesa ${table.number} abierta`,
    });
  }
};

  const handleRequestReservationSend = useCallback(
    async (reservationId: string, orderId: string) => {
      if (!apiEnabled) {
        toast.error("Disponible sólo con API");
        return;
      }
      try {
        await posApi.createReservationSendRequest({ reservationId, orderId });
        toast.success("Solicitud enviada a administración");
      } catch (error) {
        reportApiError(error);
      }
    },
    [apiEnabled, reportApiError],
  );

  const sendToKitchenNow = useCallback(
   (
  table: Table | null,
  items: OrderItem[],
  options?: { reservationId?: string | null; takeawayOrderId?: string | null },
) => {
      const reservationId = options?.reservationId ?? null;
      const takeawayOrderId = options?.takeawayOrderId ?? selectedTakeawayOrderId;
      const isTakeaway = table === null;
      const isReservationPreOrder = Boolean(reservationId);
      const existingOrder = reservationId
  
  ? ordersData.find(
      (o) =>
        o.reservationId === reservationId &&
        o.status !== "paid" &&
        o.status !== "closed",
    )
  : isTakeaway
    ? (takeawayOrderId
        ? ordersData.find(
            (o) =>
              o.id === takeawayOrderId &&
              !o.tableId &&
              !o.reservationId &&
              o.status !== "paid" &&
              o.status !== "closed",
          ) ?? selectedTakeawayOrder
        : selectedTakeawayOrder)
    : ordersData.find(
        (o) =>
          o.tableId === table?.id &&
          !o.reservationId &&
          o.status !== "paid" &&
          o.status !== "closed",
      );

      const targetStatus: Order["status"] = "in-kitchen";

      upsertOrderForTable(
        table,
        items.map((i) =>
          i.status === "pending" ? { ...i, status: "sent" } : i,
        ),
        targetStatus,
        { updateTable: !isReservationPreOrder, reservationId, takeawayOrderId, send_to_kitchen: true },
      );
      if (items.length > 0) {
       appendActivity({
  actor: currentUserSafe.name,
  actorRole: currentUserSafe.role,
  entityType: "order",
  entityId: existingOrder?.id,
  fromStatus: existingOrder?.status ?? "open",
  toStatus: targetStatus,
  action:
    targetStatus === "pending-payment"
      ? "payment_requested"
      : "order_sent",
  tableNumber: table?.number,
  details: isTakeaway
    ? `Pedido para ${existingOrder?.clientName ?? "Takeaway"} enviado a cocina (${items.length} items)`
    : `Pedido enviado a cocina (${items.length} items)`,
});
      }
    },
    [
  appendActivity,
  currentUserSafe.name,
  currentUserSafe.role,
  ordersData,
  selectedTakeawayOrder,
  selectedTakeawayOrderId,
  upsertOrderForTable,
]
  );

  const handleSendToKitchen = useCallback(
    (table: Table | null, items: OrderItem[]) => {
      sendToKitchenNow(table, items, { reservationId: selectedReservationId, takeawayOrderId: selectedTakeawayOrderId });
      const targetTakeaway = selectedTakeawayOrderId
        ? ordersData.find((o) => o.id === selectedTakeawayOrderId)
        : selectedTakeawayOrder;
      const clientName = selectedReservationId
        ? getReservationById(selectedReservationId)?.reserverName
        : targetTakeaway?.clientName;
      toast.success("Pedido enviado a cocina", {
        description: table
          ? `Mesa ${table.number} - ${items.length} items`
          : `${clientName ?? "Cliente"} - ${items.length} items`,
      });
    },
    [getReservationById, ordersData, selectedReservationId, selectedTakeawayOrder, selectedTakeawayOrderId, sendToKitchenNow],
  );

  useEffect(() => {
    if (!apiEnabled) return;
    if (!apiOnline) return;
    if (scheduledKitchenSends.length === 0) return;

    const timer = setInterval(() => {
      const nowMs = Date.now();
      const due = scheduledKitchenSends.filter((s) => {
        const t = Date.parse(s.executeAt);
        return Number.isFinite(t) && t <= nowMs;
      });
      if (due.length === 0) return;
      const executedIds = new Set<string>();

      for (const s of due) {
        const table = tablesData.find((t) => t.id === s.tableId) ?? null;
        const order =
          (s.orderId
            ? ordersData.find(
                (o) =>
                  o.id === s.orderId &&
                  o.status !== "paid" &&
                  o.status !== "closed",
              )
            : null) ??
          (s.reservationId
            ? ordersData.find(
                (o) =>
                  o.reservationId === s.reservationId &&
                  o.status !== "paid" &&
                  o.status !== "closed",
              )
            : null) ??
          ordersData.find(
            (o) =>
              o.tableId === s.tableId &&
              !o.reservationId &&
              o.status !== "paid" &&
              o.status !== "closed",
          ) ??
          null;
        if (!table || !order) continue;
        if (order.status !== "open") continue;
        sendToKitchenNow(table, order.items, {
          reservationId: s.reservationId ?? order.reservationId ?? null,
        });
        toast.success("Pedido enviado a cocina", {
          description: `Mesa ${table.number} - ${order.items.length} items`,
        });
        executedIds.add(s.id);
      }

      setScheduledKitchenSends((prev) => {
        const cutoffMs = nowMs - 24 * 60 * 60 * 1000;
        return prev.filter((s) => {
          const t = Date.parse(s.executeAt);
          if (!Number.isFinite(t)) return false;
          if (t < cutoffMs) return false;
          if (executedIds.has(s.id)) return false;
          return true;
        });
      });
    }, 1_000);

    return () => clearInterval(timer);
  }, [
    apiEnabled,
    apiOnline,
    ordersData,
    scheduledKitchenSends,
    sendToKitchenNow,
    tablesData,
  ]);

  const handleKitchenOrderStatusChange = (
    orderId: string,
    status: Order["status"],
  ) => {
    const order = ordersData.find((o) => o.id === orderId);
    const updatedAt = new Date();
    let pendingOrder: Order | null = null;

    setOrdersData((prev) =>
      prev.map((o) => {
        if (o.id !== orderId) return o;
        if (status !== "ready") {
          pendingOrder = { ...o, status, updatedAt };
          return pendingOrder;
        }
        const readyAt = updatedAt;
        const nextItems: OrderItem[] = o.items.map((i) =>
          i.status === "delivered"
            ? i
            : { ...i, status: "ready" as const, readyAt: i.readyAt ?? readyAt },
        );
        pendingOrder = { ...o, status, items: nextItems, updatedAt };
        return pendingOrder;
      }),
    );
    if (pendingOrder) {
      pendingOrdersRef.current.set(orderId, {
        order: pendingOrder,
        updatedAtMs: updatedAt.getTime(),
      });
    }

    if (order && status === "ready") {
      appendActivity({
        actor: currentUserSafe.name,
        actorRole: currentUserSafe.role,
        entityType: "order",
        entityId: orderId,
        fromStatus: order.status,
        toStatus: status,
        action: "order_ready",
        tableNumber: order.tableNumber,
        details: `Pedido listo para servir`,
      });
    }

    if (order && status === "delivered") {
      appendActivity({
        actor: currentUserSafe.name,
        actorRole: currentUserSafe.role,
        entityType: "order",
        entityId: orderId,
        fromStatus: order.status,
        toStatus: status,
        action: "order_delivered",
        tableNumber: order.tableNumber,
        details: `Pedido entregado`,
      });
    }

    if (status === "ready") {
      const readyAt = new Date();
      const nextItems: OrderItem[] =
        order?.items.map((i) =>
          i.status === "delivered"
            ? i
            : { ...i, status: "ready" as const, readyAt: i.readyAt ?? readyAt },
        ) ?? [];
      void runMutation(
        {
          id: createId("q"),
          kind: "updateOrder",
          orderId,
          payload: { status, items: nextItems },
          createdAt: new Date().toISOString(),
        },
        posApi.updateOrder(orderId, { status, items: nextItems }),
      );
    } else {
      void runMutation(
        {
          id: createId("q"),
          kind: "updateOrder",
          orderId,
          payload: { status },
          createdAt: new Date().toISOString(),
        },
        posApi.updateOrder(orderId, { status }),
      );
    }
  };

  const handleUpdateOrderItems = (orderId: string, items: OrderItem[]) => {
    const order = ordersData.find((o) => o.id === orderId);
    if (order) {
      const prevById = new Map(order.items.map((i) => [i.id, i] as const));
      for (const item of items) {
        const prev = prevById.get(item.id);
        if (!prev) continue;
        if (prev.status !== "delivered" && item.status === "delivered") {
          appendActivity({
            actor: currentUserSafe.name,
            actorRole: currentUserSafe.role,
            entityType: "order-item",
            entityId: item.id,
            fromStatus: prev.status,
            toStatus: item.status,
            action: "item_delivered",
            tableNumber: order.tableNumber,
            details: `${item.productName} entregado`,
          });
        }
      }
    }

    const updatedAt = new Date();
    let pendingOrder: Order | null = null;
    setOrdersData((prev) =>
      prev.map((o) => {
        if (o.id !== orderId) return o;
        const deliveredAt = updatedAt;
        const nextItems = items.map((i) => {
          const prev = o.items.find((p) => p.id === i.id);
          if (!prev) return i;
          if (prev.status !== "delivered" && i.status === "delivered") {
            return { ...i, deliveredAt: i.deliveredAt ?? deliveredAt };
          }
          return i;
        });
        const allDelivered =
          nextItems.length > 0 &&
          nextItems.every(
            (i) => i.status === "delivered" || i.status === "cancelled",
          );
        const nextStatus: Order["status"] = allDelivered
          ? o.status === "pending-payment"
            ? "pending-payment"
            : "delivered"
          : o.status;
        pendingOrder = {
          ...o,
          items: nextItems,
          status: nextStatus,
          updatedAt,
        };
        return pendingOrder;
      }),
    );
    if (pendingOrder) {
      pendingOrdersRef.current.set(orderId, {
        order: pendingOrder,
        updatedAtMs: updatedAt.getTime(),
      });
    }

    const allDelivered =
      items.length > 0 &&
      items.every((i) => i.status === "delivered" || i.status === "cancelled");

    if (
      order &&
      allDelivered &&
      order.status !== "delivered" &&
      order.status !== "pending-payment"
    ) {
      appendActivity({
        actor: currentUserSafe.name,
        actorRole: currentUserSafe.role,
        entityType: "order",
        entityId: orderId,
        fromStatus: order.status,
        toStatus: "delivered",
        action: "order_delivered",
        tableNumber: order.tableNumber,
        details: `Pedido entregado`,
      });
    }

    const now = new Date().toISOString();
    const payload =
      allDelivered && order?.status !== "pending-payment"
        ? { items, status: "delivered" }
        : { items };
    void runMutation(
      {
        id: createId("q"),
        kind: "updateOrder",
        orderId,
        payload,
        createdAt: now,
      },
      posApi.updateOrder(orderId, payload),
    );
  };

  const handleRequestPayment = (orderId: string) => {
    const order = ordersData.find((o) => o.id === orderId);
    if (!order) return;
    const allDelivered =
      order.items.length > 0 &&
      order.items.every((i) => i.status === "delivered");
    if (!allDelivered) {
      toast.warning("Hay productos sin entregar", {
        description: "Se puede solicitar cobro igualmente",
      });
    }

    setOrdersData((prev) =>
      prev.map((o) =>
        o.id === orderId
          ? { ...o, status: "pending-payment", updatedAt: new Date() }
          : o,
      ),
    );
    setTablesData((prev) => {
      const next = prev.map((t) =>
        t.id === order.tableId
          ? { ...t, status: "pending-payment" as const }
          : t,
      );
      return next;
    });

    appendActivity({
      actor: currentUserSafe.name,
      actorRole: currentUserSafe.role,
      entityType: "order",
      entityId: orderId,
      fromStatus: order.status,
      toStatus: "pending-payment",
      action: "payment_requested",
      tableNumber: order.tableNumber,
      details: `Cobro solicitado`,
    });

    const now = new Date().toISOString();
    void runMutation(
      {
        id: createId("q"),
        kind: "updateOrder",
        orderId,
        payload: { status: "pending-payment" },
        createdAt: now,
      },
      posApi.updateOrder(orderId, { status: "pending-payment" }),
    );
    if (order.tableId) {
      void runMutation(
        {
          id: createId("q"),
          kind: "updateTable",
          tableId: order.tableId,
          payload: { status: "pending-payment" },
          createdAt: now,
        },
        posApi.updateTable(order.tableId, { status: "pending-payment" }),
      );
    }
  };

  const handlePaymentCompleted = (payment: {
    orderId: string;
    tableId?: string;
    tableNumber?: number;
    clientName?: string;
    subtotal: number;
    tax: number;
    discount: number;
    discountCode?: string;
    tip: number;
    total: number;
    method: PaymentMethod;
    customerId?: string;
    cashAmount?: number;
    cardAmount?: number;
  }) => {

    console.log(payment.tax);

    pendingOrdersRef.current.delete(payment.orderId);
    const previousOrder = ordersData.find((o) => o.id === payment.orderId);
    const previousTable = tablesData.find((t) => t.id === payment.tableId);
    setOrdersData((prev) =>
      prev.map((o) =>
        o.id === payment.orderId
          ? { ...o, status: "closed", updatedAt: new Date() }
          : o,
      ),
    );
   if (payment.tableId) {
    setTablesData((prev) =>
        prev.map((t) =>
          t.id === payment.tableId
            ? {
                ...t,
                status: "available" as const,
                currentOrderId: undefined,
                waiter: undefined,
                openedAt: undefined,
              }
            : t,
        ),
      );
    }

    const paymentLabel =
  payment.clientName ??
  (payment.tableNumber
    ? `Mesa ${payment.tableNumber}`
    : "Takeaway");

    if (payment.discount > 0) {
      appendActivity({
        actor: currentUserSafe.name,
        actorRole: currentUserSafe.role,
        entityType: "order",
        entityId: payment.orderId,
        action: "discount_applied",
        tableNumber: payment.tableNumber,
        details: `Descuento aplicado: ${formatPrice(payment.discount)}${payment.discountCode ? ` (Aut: ${payment.discountCode})` : ""}`,
      });
    }

    appendActivity({
      actor: currentUserSafe.name,
      actorRole: currentUserSafe.role,
      entityType: "order",
      entityId: payment.orderId,
      fromStatus: previousOrder?.status ?? "pending-payment",
      toStatus: "closed",
      action: "payment_received",
      tableNumber: payment.tableNumber,
     details:
        payment.method === "mixed"
          ? `${paymentLabel} · Pago recibido (mixto): ${formatPrice(payment.cashAmount ?? 0)} efectivo + ${formatPrice(payment.cardAmount ?? 0)} tarjeta`
          : `${paymentLabel} · Pago recibido (${payment.method})`
    });
    if (payment.tableId) {
      appendActivity({
        actor: currentUserSafe.name,
        actorRole: currentUserSafe.role,
        entityType: "table",
        entityId: payment.tableId,
        fromStatus: previousTable?.status ?? "pending-payment",
        toStatus: "available",
        action: "table_closed",
        tableNumber: payment.tableNumber,
        details: `Mesa ${payment.tableNumber} cerrada`,
      });
    }

    const now = new Date().toISOString();
    const paymentPayload = {
      orderId: payment.orderId,
      tableNumber: payment.tableNumber,
      clientName: payment.clientName,
      subtotal: payment.subtotal,
      tax: payment.tax,
      discount: payment.discount,
      tip: payment.tip,
      total: payment.total,
      method: payment.method,
      status: payment.method === "credit" ? "pending" : "paid",
      ...(payment.customerId ? { customerId: payment.customerId } : {}),
    };
    void runMutation(
      {
        id: createId("q"),
        kind: "createPayment",
        payload: paymentPayload,
        createdAt: now,
      },
      posApi.createPayment(paymentPayload),
    );
  };

  const handleReceiptReprint = (payment: {
    paymentId: string;
    orderId: string;
    tableNumber: number;
    total: number;
    method: PaymentMethod;
  }) => {
    appendActivity({
      actor: currentUserSafe.name,
      actorRole: currentUserSafe.role,
      entityType: "payment",
      entityId: payment.paymentId,
      action: "receipt_reprinted",
      tableNumber: payment.tableNumber,
      details: `Comprobante reimpreso (${payment.method})`,
    });
  };

  const handleAdminTableUpdate = (tableId: string, patch: Partial<Table>) => {
    const table = tablesData.find((t) => t.id === tableId);
    const nextStatus = patch.status;
    const nextBlockedReason = patch.blockedReason;

    if (table && nextStatus) {
      if (nextStatus === "occupied") {
        appendActivity({
          actor: currentUserSafe.name,
          actorRole: currentUserSafe.role,
          entityType: "table",
          entityId: tableId,
          fromStatus: table.status,
          toStatus: nextStatus,
          action: "table_opened",
          tableNumber: table.number,
          details: `Mesa ${table.number} marcada como ocupada`,
        });
      }

      if (nextStatus === "available") {
        appendActivity({
          actor: currentUserSafe.name,
          actorRole: currentUserSafe.role,
          entityType: "table",
          entityId: tableId,
          fromStatus: table.status,
          toStatus: nextStatus,
          action: "table_closed",
          tableNumber: table.number,
          details: `Mesa ${table.number} marcada como disponible`,
        });
      }

      if (nextStatus === "blocked") {
        appendActivity({
          actor: currentUserSafe.name,
          actorRole: currentUserSafe.role,
          entityType: "table",
          entityId: tableId,
          fromStatus: table.status,
          toStatus: nextStatus,
          action: "table_blocked",
          tableNumber: table.number,
          details: `Mesa ${table.number} bloqueada${nextBlockedReason ? `: ${nextBlockedReason}` : ""}`,
        });
      }

      if (table.status === "blocked" && nextStatus !== "blocked") {
        appendActivity({
          actor: currentUserSafe.name,
          actorRole: currentUserSafe.role,
          entityType: "table",
          entityId: tableId,
          fromStatus: table.status,
          toStatus: nextStatus,
          action: "table_unblocked",
          tableNumber: table.number,
          details: `Mesa ${table.number} desbloqueada`,
        });
      }
    }

    const payload: Record<string, unknown> = { ...patch };
    if (patch.layoutGx !== undefined) {
      payload.layout_gx = patch.layoutGx;
      delete payload.layoutGx;
    }
    if (patch.layoutGy !== undefined) {
      payload.layout_gy = patch.layoutGy;
      delete payload.layoutGy;
    }
    if (patch.layoutSize !== undefined) {
      payload.layout_size = patch.layoutSize;
      delete payload.layoutSize;
    }
    if (patch.layoutShape !== undefined) {
      payload.layout_shape = patch.layoutShape;
      delete payload.layoutShape;
    }

    setTablesData((prev) =>
      prev.map((t) => {
        if (t.id !== tableId) return t;
        const next = { ...t, ...patch };
        if (nextStatus === "available") {
          next.currentOrderId = undefined;
          next.waiter = undefined;
          next.openedAt = undefined;
        }
        return next;
      })
    );

    if (nextStatus === "available") {
      const orderIdToClose = table?.currentOrderId;
      if (orderIdToClose) {
        pendingOrdersRef.current.delete(orderIdToClose);
        setOrdersData((prev) =>
          prev.map((o) =>
            o.id === orderIdToClose
              ? { ...o, status: "closed" as const, closedAt: new Date() }
              : o
          )
        );
      } else {
        setOrdersData((prev) =>
          prev.map((o) =>
            o.tableId === tableId && !o.reservationId && o.status !== "paid" && o.status !== "closed"
              ? { ...o, status: "closed" as const, closedAt: new Date() }
              : o
          )
        );
      }
    }

    void runMutation(
      {
        id: createId("q"),
        kind: "updateTable",
        tableId,
        payload,
        createdAt: new Date().toISOString(),
      },
      posApi.updateTable(tableId, payload),
    );
  };

  const handleAdminTableCreate = async (payload: {
  capacity: number;
  zone: string;
  layoutGx?: number;
  layoutGy?: number;
  layoutSize?: number;
  layoutShape?: string;
}) => {
  try {
    const created = await posApi.createTable({
      capacity: payload.capacity,
      zone: payload.zone,
      status: "available",
      ...(payload.layoutGx !== undefined
        ? { layout_gx: payload.layoutGx }
        : {}),
      ...(payload.layoutGy !== undefined
        ? { layout_gy: payload.layoutGy }
        : {}),
      ...(payload.layoutSize !== undefined
        ? { layout_size: payload.layoutSize }
        : {}),
      ...(payload.layoutShape !== undefined
        ? { layout_shape: payload.layoutShape }
        : {}),
    });

    setTablesData((prev) =>
      [...prev, created].sort((a, b) => a.number - b.number),
    );

    appendActivity({
      actor: currentUserSafe.name,
      actorRole: currentUserSafe.role,
      entityType: "table",
      entityId: created.id,
      action: "table_created",
      tableNumber: created.number,
      details: `Mesa ${created.number} creada`,
    });

    return created; // <-- ESTO ES LO QUE FALTA
  } catch (error) {
    reportApiError(error);
    throw error;
  }
};

  const handleAdminTableDelete = async (tableId: string) => {
    const existing = tablesData.find((t) => t.id === tableId);
    try {
      await posApi.deleteTable(tableId);
      setTablesData((prev) => prev.filter((t) => t.id !== tableId));
      if (existing) {
        appendActivity({
          actor: currentUserSafe.name,
          actorRole: currentUserSafe.role,
          entityType: "table",
          entityId: tableId,
          action: "table_deleted",
          tableNumber: existing.number,
          details: `Mesa ${existing.number} eliminada`,
        });
      }
    } catch (error) {
      reportApiError(error);
      throw error;
    }
  };

  const handleAdminZoneCreate = async (payload: {
    name: string;
    order: number;
    layoutWidth?: number;
    layoutHeight?: number;
  }) => {
    try {
      const created = await posApi.createZone({
        name: payload.name,
        sort_order: payload.order,
        ...(payload.layoutWidth !== undefined
          ? { layout_width: payload.layoutWidth }
          : {}),
        ...(payload.layoutHeight !== undefined
          ? { layout_height: payload.layoutHeight }
          : {}),
      });
      setZonesData((prev) =>
        [...prev, created].sort(
          (a, b) => a.order - b.order || a.name.localeCompare(b.name),
        ),
      );
    } catch (error) {
      reportApiError(error);
      throw error;
    }
  };

  const handleAdminZoneUpdate = async (
    zoneId: string,
    patch: {
      name?: string;
      order?: number;
      layoutWidth?: number;
      layoutHeight?: number;
    },
  ) => {
    const existing = zonesData.find((z) => z.id === zoneId);
    try {
      const updated = await posApi.updateZone(zoneId, {
        ...(patch.name !== undefined ? { name: patch.name } : {}),
        ...(patch.order !== undefined ? { sort_order: patch.order } : {}),
        ...(patch.layoutWidth !== undefined
          ? { layout_width: patch.layoutWidth }
          : {}),
        ...(patch.layoutHeight !== undefined
          ? { layout_height: patch.layoutHeight }
          : {}),
      });
      setZonesData((prev) =>
        prev
          .map((z) => (z.id === zoneId ? updated : z))
          .sort((a, b) => a.order - b.order || a.name.localeCompare(b.name)),
      );
      if (existing && patch.name && patch.name !== existing.name) {
        setTablesData((prev) =>
          prev.map((t) =>
            t.zone === existing.name ? { ...t, zone: patch.name as string } : t,
          ),
        );
      }
    } catch (error) {
      reportApiError(error);
      throw error;
    }
  };

  const handleAdminZoneDelete = async (zoneId: string) => {
    try {
      await posApi.deleteZone(zoneId);
      setZonesData((prev) => prev.filter((z) => z.id !== zoneId));
    } catch (error) {
      reportApiError(error);
      throw error;
    }
  };

  const handleAdminProductUpdate = (
    productId: string,
    patch: Record<string, unknown> & {
      available?: boolean;
      imageFile?: File | null;
      availabilityRules?: { days: number[]; start: string; end: string }[];
      priceRules?: {
        days: number[];
        start: string;
        end: string;
        price: number;
      }[];
    },
  ) => {
    const product = productsData.find((p) => p.id === productId);
    if (
      product &&
      typeof patch.available === "boolean" &&
      patch.available !== product.available
    ) {
      appendActivity({
        actor: currentUserSafe.name,
        actorRole: currentUserSafe.role,
        entityType: "product",
        entityId: productId,
        fromStatus: product.available ? "available" : "unavailable",
        toStatus: patch.available ? "available" : "unavailable",
        action: "product_unavailable",
        details: patch.available
          ? `${product.name} marcado como disponible`
          : `${product.name} marcado como no disponible`,
      });
    }
    const updatePromise = posApi.updateProduct(productId, patch).then((updatedProduct) => {
      setProductsData((prev) =>
        prev.map((p) => (p.id === productId ? updatedProduct : p)),
      );
      return updatedProduct;
    });
    void runMutation(
      {
        id: createId("q"),
        kind: "updateProduct",
        productId,
        payload: patch,
        createdAt: new Date().toISOString(),
      },
      updatePromise,
    );
  };

  const handleAdminCategoryCreate = async (payload: {
    name: string;
    sortOrder?: number;
    stationKey?: string | null;
  }) => {
    try {
      const created = await posApi.createCategory(payload);
      setCategoriesData((prev) =>
        [...prev, created].sort((a, b) => a.order - b.order),
      );
      appendActivity({
        actor: currentUserSafe.name,
        actorRole: currentUserSafe.role,
        entityType: "category",
        entityId: created.id,
        action: "category_created",
        details: `Categoría creada: ${created.name}`,
      });
      return created;
    } catch (error) {
      reportApiError(error);
      throw error;
    }
  };

  const handleAdminCategoryUpdate = async (
    categoryId: string,
    payload: { name?: string; sortOrder?: number; stationKey?: string | null },
  ) => {
    try {
      const updated = await posApi.updateCategory(categoryId, payload);
      setCategoriesData((prev) =>
        prev
          .map((c) => (c.id === categoryId ? updated : c))
          .sort((a, b) => a.order - b.order),
      );
      appendActivity({
        actor: currentUserSafe.name,
        actorRole: currentUserSafe.role,
        entityType: "category",
        entityId: categoryId,
        action: "category_updated",
        details: `Categoría actualizada: ${updated.name}`,
      });
    } catch (error) {
      reportApiError(error);
      throw error;
    }
  };

  const handleAdminCategoryDelete = async (categoryId: string) => {
    const existing = categoriesData.find((c) => c.id === categoryId);
    try {
      await posApi.deleteCategory(categoryId);
      setCategoriesData((prev) => prev.filter((c) => c.id !== categoryId));
      if (existing) {
        appendActivity({
          actor: currentUserSafe.name,
          actorRole: currentUserSafe.role,
          entityType: "category",
          entityId: categoryId,
          action: "category_deleted",
          details: `Categoría eliminada: ${existing.name}`,
        });
      }
    } catch (error) {
      reportApiError(error);
      throw error;
    }
  };

  const handleAdminProductCreate = async (
    payload: Parameters<typeof posApi.createProduct>[0],
  ) => {
    try {
      const created = await posApi.createProduct(payload);
      setProductsData((prev) =>
        [...prev, created].sort((a, b) => a.name.localeCompare(b.name)),
      );
      appendActivity({
        actor: currentUserSafe.name,
        actorRole: currentUserSafe.role,
        entityType: "product",
        entityId: created.id,
        action: "product_created",
        details: `Producto creado: ${created.name}`,
      });
    } catch (error) {
      reportApiError(error);
      throw error;
    }
  };

  const handleAdminProductDelete = async (productId: string) => {
    const existing = productsData.find((p) => p.id === productId);
    try {
      await posApi.deleteProduct(productId);
      setProductsData((prev) => prev.filter((p) => p.id !== productId));
      if (existing) {
        appendActivity({
          actor: currentUserSafe.name,
          actorRole: currentUserSafe.role,
          entityType: "product",
          entityId: productId,
          action: "product_deleted",
          details: `Producto eliminado: ${existing.name}`,
        });
      }
    } catch (error) {
      reportApiError(error);
      throw error;
    }
  };

  const pendingSyncTableIds = useMemo(() => {
    const ids = offlineQueue
      .filter(
        (m): m is Extract<QueuedMutation, { kind: "updateTable" }> =>
          m.kind === "updateTable",
      )
      .map((m) => m.tableId);
    return Array.from(new Set(ids));
  }, [offlineQueue]);

  const renderView = () => {  
    switch (activeView) {
      case "tables":
        return (
          <div className="flex h-full gap-6">
            <div className="flex-1 min-w-0 flex flex-col gap-4 min-h-0">
              <Tabs defaultValue="tables" className="flex-1 flex flex-col min-h-0">
                <TabsList className="mb-2 w-fit">
                  <TabsTrigger value="tables">Mesas</TabsTrigger>
                  <TabsTrigger value="reservations" className="flex items-center gap-2">
                    Reservaciones
                    {apiEnabled && reservationsData.filter((r) => r.status === "scheduled").length > 0 && (
                      <Badge className="bg-primary text-primary-foreground font-bold px-1.5 py-0.5 text-[10px] rounded-full shrink-0">
                        {reservationsData.filter((r) => r.status === "scheduled").length}
                      </Badge>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="takeaway" className="flex items-center gap-2">
                    Para llevar
                    {activeTakeawayOrders.length > 0 && (
                      <Badge className="bg-primary text-primary-foreground font-bold px-1.5 py-0.5 text-[10px] rounded-full shrink-0">
                        {activeTakeawayOrders.length}
                      </Badge>
                    )}
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="tables" className="flex-1 min-h-0 flex flex-col outline-none">
                <TablesView
                  selectedTableId={selectedTableId}
                  onTableSelect={(t) => {
                    if (t.status === "blocked") {
                      setSelectedTableId(null);
                      setSelectedTakeawayOrderId(null);
                      setUnlockReservedTableCandidate(t);
                      setUnlockReservedTableCodeText("");
                      setSelectedReservationId(null);
                      return;
                    }
                    if (t.status === "available") {
                      setReservationActionCandidate(t);
                      setSelectedReservationId(null);
                      setSelectedTakeawayOrderId(null);
                      return;
                    }
                    setSelectedTableId(t.id);
                    setSelectedReservationId(null);
                    setSelectedTakeawayOrderId(null);
                  }}
                  onTakeawaySelect={() => {
                    setOpenTakeawayClient(true);
                    setSelectedTableId(null);
                    setSelectedReservationId(null);
                  }}
                  tables={tablesData}
                  orders={ordersData}
                  zones={derivedZones}
                  pendingSyncTableIds={pendingSyncTableIds}
                />
                </TabsContent>

                <TabsContent value="reservations" className="flex-1 min-h-0 flex flex-col outline-none">
                  <Card className="flex-1 min-h-0 flex flex-col border-none shadow-none bg-transparent">
                <CardHeader className="py-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-sm font-bold">
                      Próximas reservaciones
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="default"
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold gap-1 shadow-sm"
                        onClick={startTablelessReservation}
                        disabled={!apiEnabled}
                      >
                        <Plus className="h-4 w-4" />
                        Agregar Reserva Sin Mesa
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setShowReservationHistory((v) => {
                            const next = !v;
                            if (next) void refreshReservationsHistoryFromApi();
                            return next;
                          });
                        }}
                        disabled={!apiEnabled}
                      >
                        Historial
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleDownloadReservationsReport}
                        disabled={!apiEnabled}
                        title="Descargar reporte"
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => void refreshReservationsFromApi()}
                        disabled={!apiEnabled}
                      >
                        Actualizar
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0 h-full min-h-0">
                  <ScrollArea className="h-full pr-2">
                    {!apiEnabled ? (
                      <div className="text-sm text-muted-foreground py-6 text-center">
                        Reservas disponibles sólo con API
                      </div>
                    ) : (
                      (() => {
                        const upcoming = reservationsData
                          .filter((r) => r.status === "scheduled")
                          .sort(
                            (a, b) =>
                              toMs(a.reservedAt) - toMs(b.reservedAt),
                          )
                          .slice(0, 50);
                        if (upcoming.length === 0) {
                          return (
                            <div className="flex flex-col items-center justify-center py-10 gap-3 text-center">
                              <div className="text-sm text-muted-foreground">
                                Sin reservas próximas
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                className="gap-1.5 border-dashed border-emerald-600/50 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/30"
                                onClick={startTablelessReservation}
                              >
                                <Plus className="h-4 w-4" />
                                Crear Reserva Sin Mesa
                              </Button>
                            </div>
                          );
                        }
                        return (
                          <div className="space-y-2 pb-4">
                            {upcoming.map((r) => {
                              const isTableless = !r.tableId;
                              const rMs = toMs(r.reservedAt);
                              const isLate = rMs > 0 && rMs <= Date.now();
                              const lateMins = isLate ? Math.floor((Date.now() - rMs) / 60000) : 0;
                              return (
                                <div
                                  key={r.id}
                                  className={cn(
                                    "rounded-lg border px-3 py-2 transition-all",
                                    isLate
                                      ? "border-red-500/80 bg-red-500/10 dark:bg-red-950/40 shadow-md shadow-red-500/10"
                                      : "border-border bg-card/40"
                                  )}
                                >
                                  <div className="flex items-center justify-between gap-3">
                                    <div className="min-w-0">
                                      <div className="flex items-center gap-2 min-w-0 flex-wrap">
                                        <Badge
                                          variant="outline"
                                          className={
                                            r.tableId
                                              ? "shrink-0"
                                              : isLate
                                              ? "shrink-0 bg-red-500/20 text-red-400 border-red-500/40 font-bold"
                                              : "shrink-0 bg-amber-500/20 text-amber-500 border-amber-500/30"
                                          }
                                        >
                                          {r.tableNumber ? `Mesa ${r.tableNumber}` : "Sin Mesa"}
                                        </Badge>
                                        {r.zone && (
                                          <span className="text-xs text-muted-foreground shrink-0">
                                            {r.zone}
                                          </span>
                                        )}
                                        <span className="text-sm font-semibold truncate">
                                          {r.reserverName}
                                        </span>
                                        {isLate && (
                                          <Badge variant="destructive" className="text-[10px] bg-red-600 text-white font-black animate-pulse">
                                            ⚠️ Atraso del Cliente ({lateMins} min)
                                          </Badge>
                                        )}
                                      </div>
                                    <div className="text-xs text-muted-foreground mt-0.5">
                                      {r.partySize} comensales · bloqueo{" "}
                                      {r.preBlockMinutes} min antes · cocina{" "}
                                      {r.kitchenLeadMinutes} min antes · código{" "}
                                      {r.unlockCodeHint ?? "--"}
                                      {r.items && r.items.length > 0 && (
                                        <span className="font-medium text-foreground ml-1">
                                          · {r.items.length} platos pre-guardados
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <div className="shrink-0 flex flex-col items-end gap-2">
                                    <div className="text-xs font-mono text-muted-foreground">
                                      {r.reservedAt.toLocaleString()}
                                    </div>
                                    <div className="flex items-center gap-2">
                                      {!r.tableId && (
                                        <Button
                                          size="sm"
                                          variant="default"
                                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                          onClick={() => setAssignTableReservationCandidate(r)}
                                        >
                                          Asignar Mesa
                                        </Button>
                                      )}
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => {
                                          setSelectedTableId(r.tableId ?? null);
                                          setSelectedReservationId(r.id);
                                          setSelectedTakeawayOrderId(null);
                                        }}
                                      >
                                        Comanda
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => startEditReservation(r)}
                                      >
                                        Editar
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="destructive"
                                        onClick={() =>
                                          setCancelReservationCandidate(r)
                                        }
                                      >
                                        Anular
                                      </Button>
                                    </div>
                                  </div>
                                    </div>
                                  </div>
                              );
                            })}
                          </div>
                        );
                      })()
                    )}
                    {apiEnabled && showReservationHistory && (
                      <div className="border-t border-border pt-4 mt-4 space-y-3 pb-6">
                        <div className="flex items-center justify-between gap-3">
                          <div className="text-sm font-bold">Historial</div>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleDownloadReservationsHistoryExcel}
                              disabled={
                                !apiEnabled ||
                                reservationsHistoryLoading ||
                                reservationsHistoryData.length === 0
                              }
                              title="Descargar para Excel"
                              className="border-emerald-500/60 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800 dark:text-emerald-300 dark:hover:bg-emerald-500/10"
                            >
                              Excel
                            </Button>
                            <Button
                              variant="default"
                              size="sm"
                              onClick={() =>
                                void refreshReservationsHistoryFromApi()
                              }
                              disabled={
                                !apiEnabled || reservationsHistoryLoading
                              }
                              className="bg-indigo-600 hover:bg-indigo-700"
                            >
                              Buscar
                            </Button>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                          <div className="space-y-1">
                            <div className="text-xs font-medium text-foreground">
                              Desde
                            </div>
                            <Input
                              type="date"
                              value={reservationsHistoryFromDate}
                              onChange={(e) =>
                                setReservationsHistoryFromDate(e.target.value)
                              }
                            />
                          </div>
                          <div className="space-y-1">
                            <div className="text-xs font-medium text-foreground">
                              Hasta
                            </div>
                            <Input
                              type="date"
                              value={reservationsHistoryToDate}
                              onChange={(e) =>
                                setReservationsHistoryToDate(e.target.value)
                              }
                            />
                          </div>
                          <div className="space-y-1">
                            <div className="text-xs font-medium text-foreground">
                              Estado
                            </div>
                            <select
                              className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                              value={reservationsHistoryStatus}
                              onChange={(e) =>
                                setReservationsHistoryStatus(
                                  e.target
                                    .value as typeof reservationsHistoryStatus,
                                )
                              }
                            >
                              <option value="all">Todos</option>
                              <option value="scheduled">Programada</option>
                              <option value="seated">Asistió</option>
                              <option value="cancelled">Anulada</option>
                            </select>
                          </div>
                        </div>
                        {(() => {
                          const total = reservationsHistoryData.length;
                          const scheduled = reservationsHistoryData.filter(
                            (r) => r.status === "scheduled",
                          ).length;
                          const seated = reservationsHistoryData.filter(
                            (r) => r.status === "seated",
                          ).length;
                          const cancelled = reservationsHistoryData.filter(
                            (r) => r.status === "cancelled",
                          ).length;
                          const avgParty =
                            total > 0
                              ? Math.round(
                                  (reservationsHistoryData.reduce(
                                    (sum, r) => sum + (r.partySize || 0),
                                    0,
                                  ) /
                                    total) *
                                    10,
                                ) / 10
                              : 0;
                          return (
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                              <div className="rounded-lg border border-border bg-card/40 px-3 py-2">
                                <div className="text-[10px] text-muted-foreground">
                                  Total
                                </div>
                                <div className="text-sm font-bold">{total}</div>
                              </div>
                              <div className="rounded-lg border border-border bg-card/40 px-3 py-2">
                                <div className="text-[10px] text-muted-foreground">
                                  Programadas
                                </div>
                                <div className="text-sm font-bold">
                                  {scheduled}
                                </div>
                              </div>
                              <div className="rounded-lg border border-border bg-card/40 px-3 py-2">
                                <div className="text-[10px] text-muted-foreground">
                                  Asistió
                                </div>
                                <div className="text-sm font-bold">
                                  {seated}
                                </div>
                              </div>
                              <div className="rounded-lg border border-border bg-card/40 px-3 py-2">
                                <div className="text-[10px] text-muted-foreground">
                                  Prom. comensales
                                </div>
                                <div className="text-sm font-bold">
                                  {avgParty}
                                </div>
                              </div>
                              <div className="rounded-lg border border-border bg-card/40 px-3 py-2">
                                <div className="text-[10px] text-muted-foreground">
                                  Anuladas
                                </div>
                                <div className="text-sm font-bold">
                                  {cancelled}
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                        {reservationsHistoryLoading ? (
                          <div className="text-sm text-muted-foreground py-2 text-center">
                            Cargando historial...
                          </div>
                        ) : reservationsHistoryData.length === 0 ? (
                          <div className="text-sm text-muted-foreground py-2 text-center">
                            Sin datos en el rango
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {reservationsHistoryData.slice(0, 200).map((r) => {
                              const order =
                                ordersData.find(
                                  (o) =>
                                    o.reservationId === r.id &&
                                    o.status !== "paid" &&
                                    o.status !== "closed",
                                ) ?? null;
                              const itemsCount = order
                                ? order.items.filter(
                                    (i) => i.status !== "cancelled",
                                  ).length
                                : 0;
                              return (
                                <div
                                  key={`hist-${r.id}`}
                                  className="rounded-lg border border-border bg-card/40 px-3 py-2"
                                >
                                  <div className="flex items-center justify-between gap-3">
                                    <div className="min-w-0">
                                      <div className="flex items-center gap-2 min-w-0">
                                        <Badge
                                          variant="outline"
                                          className="shrink-0"
                                        >
                                          Mesa {r.tableNumber}
                                        </Badge>
                                        <span className="text-xs text-muted-foreground shrink-0">
                                          {r.zone}
                                        </span>
                                        <span className="text-sm font-semibold truncate">
                                          {r.reserverName}
                                        </span>
                                      </div>
                                      <div className="text-xs text-muted-foreground mt-0.5">
                                        {r.status} · {r.partySize} comensales ·{" "}
                                        {itemsCount} items
                                      </div>
                                    </div>
                                    <div className="shrink-0 flex flex-col items-end gap-2">
                                      <div className="text-xs font-mono text-muted-foreground">
                                        {toMs(r.reservedAt) ? new Date(toMs(r.reservedAt)).toLocaleString() : ""}
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => {
                                            setSelectedTableId(r.tableId ?? null);
                                            setSelectedReservationId(r.id);
                                            setSelectedTakeawayOrderId(null);
                                          }}
                                        >
                                          Comanda
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>
                </TabsContent>

                <TabsContent value="takeaway" className="flex-1 min-h-0 flex flex-col outline-none">
                  <div className="flex-1 min-h-0 flex flex-col gap-4">
                    {/* Header bar inside the takeaway tab */}
                    <div className="flex items-center justify-between gap-3 shrink-0 py-2">
                      <div className="text-sm font-bold">
                        Órdenes activas para llevar
                      </div>
                      <Button
                        size="sm"
                        className="font-bold flex items-center gap-1.5"
                        onClick={() => {
                          setOpenTakeawayClient(true);
                          setSelectedTableId(null);
                          setSelectedReservationId(null);
                          setSelectedTakeawayOrderId(null);
                        }}
                      >
                        <Plus className="w-3.5 h-3.5" />
                        Nueva orden para llevar
                      </Button>
                    </div>

                    {/* Scrollable list of takeaway order cards */}
                    <ScrollArea className="flex-1 pr-2">
                      {activeTakeawayOrders.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-16 text-center border border-dashed border-border rounded-2xl bg-card/20">
                          <ShoppingBag className="w-8 h-8 text-muted-foreground/60 mb-2" />
                          <p className="text-sm text-muted-foreground">
                            No hay órdenes activas para llevar.
                          </p>
                          <Button
                            variant="link"
                            className="mt-2 text-xs font-semibold"
                            onClick={() => setOpenTakeawayClient(true)}
                          >
                            Crear una nueva orden
                          </Button>
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 pb-6">
                          {activeTakeawayOrders.map((order) => {
                            const elapsed = minutesAgo(order.createdAt);
                            const totalItems = order.items.reduce((acc, it) => acc + it.quantity, 0);

                            // Status color/badges matching tables
                            const statusStyles = (() => {
                              switch (order.status) {
                                case "open":
                                  return "bg-slate-500/10 text-slate-400 border-slate-500/20";
                                case "in-kitchen":
                                  return "bg-amber-500/10 text-amber-500 border-amber-500/20";
                                case "ready":
                                  return "bg-emerald-500/10 text-emerald-500 border-emerald-500/20";
                                case "pending-payment":
                                  return "bg-indigo-500/10 text-indigo-500 border-indigo-500/20";
                                default:
                                  return "bg-slate-500/10 text-slate-400 border-slate-500/20";
                              }
                            })();

                            const statusLabel = (() => {
                              switch (order.status) {
                                case "open":
                                  return "Tomando";
                                case "in-kitchen":
                                  return "En cocina";
                                case "ready":
                                  return "Listo";
                                case "pending-payment":
                                  return "Cobro";
                                default:
                                  return order.status;
                              }
                            })();

                            const isSelected = selectedTakeawayOrderId === order.id;

                            return (
                              <button
                                key={order.id}
                                onClick={() => {
                                  setSelectedTakeawayOrderId(order.id);
                                  setSelectedTableId(null);
                                  setSelectedReservationId(null);
                                }}
                                className={cn(
                                  "relative aspect-square sm:aspect-auto sm:h-36 rounded-2xl border-2 transition-all duration-300 group overflow-hidden",
                                  "flex flex-col items-center justify-center gap-2 sm:gap-3 p-3 sm:p-4",
                                  isSelected
                                    ? "border-primary bg-primary/5 shadow-xl shadow-primary/10 ring-2 ring-primary/20"
                                    : "border-border bg-card hover:border-primary/50 hover:bg-muted/30"
                                )}
                              >
                                {/* Status Indicator Bar */}
                                <div
                                  className={cn(
                                    "absolute top-0 left-0 right-0 h-1 sm:h-1.5",
                                    order.status === "open" && "bg-slate-500",
                                    order.status === "in-kitchen" && "bg-amber-500",
                                    order.status === "ready" && "bg-emerald-500",
                                    order.status === "pending-payment" && "bg-violet-500"
                                  )}
                                />

                                {/* Shopping Bag Icon with status classes */}
                                <div
                                  className={cn(
                                    "w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center transition-transform duration-300 group-hover:scale-110",
                                    order.status === "open" && "bg-slate-500/10 text-slate-400",
                                    order.status === "in-kitchen" && "bg-amber-500/10 text-amber-500",
                                    order.status === "ready" && "bg-emerald-500/10 text-emerald-500",
                                    order.status === "pending-payment" && "bg-violet-500/10 text-violet-500"
                                  )}
                                >
                                  <ShoppingBag className="w-5 h-5 sm:w-6 sm:h-6" />
                                </div>

                                {/* Info Text */}
                                <div className="text-center min-w-0 w-full">
                                  <span className="text-[10px] sm:text-xs font-bold text-foreground uppercase tracking-wider block truncate">
                                    {order.clientName || "Para llevar"}
                                  </span>

                                  {/* Price Badge */}
                                  <span
                                    className={cn(
                                      "text-[9px] sm:text-[10px] font-mono font-bold px-1.5 rounded truncate inline-block mt-0.5 border",
                                      order.status === "open" && "text-slate-400 bg-slate-500/10 border-slate-500/20",
                                      order.status === "in-kitchen" && "text-amber-500 bg-amber-500/10 border-amber-500/20",
                                      order.status === "ready" && "text-emerald-500 bg-emerald-500/10 border-emerald-500/20",
                                      order.status === "pending-payment" && "text-violet-500 bg-violet-500/10 border-violet-500/20"
                                    )}
                                  >
                                    {formatPrice(order.total)}
                                  </span>

                                  {/* Elapsed / status detail */}
                                  <div className="mt-1.5 flex flex-col gap-0.5 text-[8px] sm:text-[9px] leading-none w-full items-center opacity-85">
                                    {elapsed && (
                                      <div className="flex items-center gap-1 text-muted-foreground justify-center">
                                        <Clock className="w-2.5 h-2.5 shrink-0 text-muted-foreground/60" />
                                        <span>{elapsed}</span>
                                      </div>
                                    )}
                                    {order.status === "in-kitchen" && (
                                      <div className="flex items-center gap-1 text-amber-500 font-medium justify-center mt-0.5">
                                        <ChefHat className="w-2.5 h-2.5 shrink-0 text-amber-500/70" />
                                        <span>Cocina</span>
                                      </div>
                                    )}
                                    {order.status === "ready" && (
                                      <div className="flex items-center gap-1 text-emerald-500 font-medium justify-center mt-0.5">
                                        <CheckCircle2 className="w-2.5 h-2.5 shrink-0 text-emerald-500/70" />
                                        <span>Listo</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </ScrollArea>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
            {(selectedTable || selectedTakeawayOrder || selectedReservationId) && (
              <OrderPanel
                table={selectedTable}
                takeawayOrder={selectedTakeawayOrder}
                onClose={() => {
                  setSelectedTableId(null);
                  setSelectedTakeawayOrderId(null);
                  setSelectedReservationId(null);
                }}
                onTransferTable={setTransferOriginTable}
                onDeleteOrder={handleDeleteOrder}
                categories={categoriesData}
                products={productsData}
                orders={ordersData}
                currentUser={currentUserSafe}
                onSaveOrder={handleSaveOrder}
                onSendToKitchen={handleSendToKitchen}
                sendToKitchenDisabledReason={undefined}
                reservationId={
                  selectedTakeawayOrder
                    ? undefined
                    : selectedReservationId ?? undefined
                }
                reservationSendMode={(() => {
                  if (selectedTakeawayOrder) {
                    return undefined;
                  }

                  return selectedReservationId
                    ? getReservationById(selectedReservationId)?.sendMode
                    : selectedTable
                    ? getUpcomingReservationForTable(selectedTable.id)?.sendMode
                    : undefined;
                })()}
                onRequestReservationSend={handleRequestReservationSend}
                onUpdateOrderItems={handleUpdateOrderItems}
                onRequestPayment={handleRequestPayment}
              />
            )}
          </div>
        );
      case "kitchen":
        return (
          <KitchenView
            orders={ordersData}
            tables={tablesData}
            products={productsData}
            categories={categoriesData}
            stationKey="cocina"
            activityLogs={activityLogsData}
            reservations={reservationsData}
            onOrderStatusChange={handleKitchenOrderStatusChange}
            onOrderItemsChange={handleUpdateOrderItems}
            onTicketScan={
              apiEnabled
                ? async (ticketId) => {
                    await posApi.markStationTicketReady(ticketId);
                    await refreshFromApi();
                  }
                : undefined
            }
            onTicketReprint={
              apiEnabled
                ? async (ticketId) => {
                    await posApi.reprintStationTicket(ticketId);
                  }
                : undefined
            }
          />
        );
      case "bar":
        return (
          <KitchenView
            orders={ordersData}
            tables={tablesData}
            products={productsData}
            categories={categoriesData}
            stationKey="bar"
            activityLogs={activityLogsData}
            reservations={reservationsData}
            onOrderStatusChange={handleKitchenOrderStatusChange}
            onOrderItemsChange={handleUpdateOrderItems}
            onTicketScan={
              apiEnabled
                ? async (ticketId) => {
                    await posApi.markStationTicketReady(ticketId);
                    await refreshFromApi();
                  }
                : undefined
            }
            onTicketReprint={
              apiEnabled
                ? async (ticketId) => {
                    await posApi.reprintStationTicket(ticketId);
                  }
                : undefined
            }
          />
        );
      case "cashier":
        return (
          <CashierView
            tables={tablesData}
            orders={ordersData}
            onPaymentCompleted={handlePaymentCompleted}
            onReceiptReprint={handleReceiptReprint}
          />
        );
      case "admin":
        return (
          <AdminView
            activeTab={adminTab}
            onTabChange={setAdminTab}
            activityLogs={activityLogsData}
            categories={categoriesData}
            currentShift={null}
            currentUser={currentUserSafe}
            products={productsData}
            tables={tablesData}
            orders={ordersData}
            users={[]}
            customers={[]}
            zones={derivedZones}
            zoneItems={zonesData}
            stationPrinters={stationPrintersData}
            onStationPrinterUpdate={
              apiEnabled
                ? async (stationKey, patch) => {
                    await posApi.updateStationPrinter(stationKey, patch);
                    await refreshFromApi();
                  }
                : undefined
            }
            onStationPrinterTestPrint={
              apiEnabled
                ? async (stationKey) =>
                    await posApi.testStationPrinter(stationKey)
                : undefined
            }
            onTablesChange={setTablesData}
            onProductsChange={setProductsData}
            onTableUpdate={apiEnabled ? handleAdminTableUpdate : undefined}
            onTableCreate={apiEnabled ? handleAdminTableCreate : undefined}
            onTableDelete={apiEnabled ? handleAdminTableDelete : undefined}
            onZoneCreate={apiEnabled ? handleAdminZoneCreate : undefined}
            onZoneUpdate={apiEnabled ? handleAdminZoneUpdate : undefined}
            onZoneDelete={apiEnabled ? handleAdminZoneDelete : undefined}
            onProductUpdate={apiEnabled ? handleAdminProductUpdate : undefined}
            onProductCreate={apiEnabled ? handleAdminProductCreate : undefined}
            onProductDelete={apiEnabled ? handleAdminProductDelete : undefined}
            onCategoryCreate={
              apiEnabled ? handleAdminCategoryCreate : undefined
            }
            onCategoryUpdate={
              apiEnabled ? handleAdminCategoryUpdate : undefined
            }
            onCategoryDelete={
              apiEnabled ? handleAdminCategoryDelete : undefined
            }
            onCustomerCreate={
              apiEnabled
                ? async (payload) => {
                    await posApi.createCustomer(payload);
                    await refreshAuxFromApi();
                  }
                : undefined
            }
            onCustomerUpdate={
              apiEnabled
                ? async (customerId, payload) => {
                    await posApi.updateCustomer(customerId, payload);
                    await refreshAuxFromApi();
                  }
                : undefined
            }
            onCustomerDelete={
              apiEnabled
                ? async (customerId) => {
                    await posApi.deleteCustomer(customerId);
                    await refreshAuxFromApi();
                  }
                : undefined
            }
          />
        );
      default:
        return (
          <div className="flex h-full gap-6">
            <div className="flex-1 min-w-0">
              <TablesView
                selectedTableId={selectedTableId}
                onTableSelect={(t) => {
                  if (t.status === "blocked") {
                    setSelectedTableId(null);
                    setSelectedTakeawayOrderId(null);
                    setUnlockReservedTableCandidate(t);
                    setUnlockReservedTableCodeText("");
                    return;
                  }
                  if (t.status === "available") {
                    setOpenTableCandidate(t);
                    setOpenTableDinersText("");
                    setSelectedReservationId(null);
                    setSelectedTakeawayOrderId(null);
                    return;
                  }
                  setSelectedTableId(t.id);
                  setSelectedReservationId(null);
                  setSelectedTakeawayOrderId(null);
                }}
                onTakeawaySelect={() => {
                  setOpenTakeawayClient(true);
                  setSelectedTableId(null);
                  setSelectedReservationId(null);
                }}
                tables={tablesData}
                orders={ordersData}
                zones={derivedZones}
                pendingSyncTableIds={pendingSyncTableIds}
              />
            </div>
            {(selectedTable || selectedTakeawayOrder || selectedReservationId) && (
              <OrderPanel
                table={selectedTable}
                takeawayOrder={selectedTakeawayOrder}
                onClose={() => {
                  setSelectedTableId(null);
                  setSelectedTakeawayOrderId(null);
                  setSelectedReservationId(null);
                }}
                onTransferTable={setTransferOriginTable}
                onDeleteOrder={handleDeleteOrder}
                categories={categoriesData}
                products={productsData}
                orders={ordersData}
                currentUser={currentUserSafe}
                onSaveOrder={handleSaveOrder}
                onSendToKitchen={handleSendToKitchen}
                sendToKitchenDisabledReason={(() => {
                  const reservation = selectedReservationId
                    ? getReservationById(selectedReservationId)
                    : selectedTable
                    ? getUpcomingReservationForTable(selectedTable.id)
                    : null;
                  const isReservationPreOrder =
                    Boolean(reservation) && selectedTable?.status !== "occupied";
                  if (isReservationPreOrder) {
                    return reservation?.sendMode === "manual"
                      ? "Comanda de reserva: solicita envío para aprobación de administración"
                      : "Comanda de reserva: envío a cocina automático según tiempo configurado";
                  }
                  return undefined;
                })()}
                reservationId={
                  selectedTakeawayOrder
                    ? undefined
                    : selectedReservationId ?? undefined
                }
                reservationSendMode={(() => {
                  if (selectedTakeawayOrder) {
                    return undefined;
                  }

                  return selectedReservationId
                    ? getReservationById(selectedReservationId)?.sendMode
                    : selectedTable
                    ? getUpcomingReservationForTable(selectedTable.id)?.sendMode
                    : undefined;
                })()}
                onRequestReservationSend={handleRequestReservationSend}
                onUpdateOrderItems={handleUpdateOrderItems}
                onRequestPayment={handleRequestPayment}
              />
            )}
          </div>
        );
    }
  };

  if (!currentUser && apiEnabledStatic) return null;

  return (
    <POSLayout
      activeView={activeView}
      onViewChange={handleViewChange}
      currentUser={currentUserSafe}
      apiOnline={apiEnabled ? apiOnline : true}
      onRetryApi={apiEnabled ? () => void refreshFromApi() : undefined}
      onLogout={authedUser ? () => void logout() : undefined}
      adminTab={adminTab}
      onAdminTabChange={setAdminTab}
    >
      {renderView()}
      <Dialog
        open={Boolean(unlockReservedTableCandidate)}
        onOpenChange={(open) => {
          if (!open) {
            setUnlockReservedTableCandidate(null);
            setUnlockReservedTableCodeText("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {(() => {
                if (!unlockReservedTableCandidate) return "Mesa bloqueada";
                const isReservationBlocked = (
                  unlockReservedTableCandidate.blockedReason ?? ""
                ).startsWith("Reservada");
                return isReservationBlocked
                  ? `Mesa ${unlockReservedTableCandidate.number} reservada`
                  : `Mesa ${unlockReservedTableCandidate.number} bloqueada`;
              })()}
            </DialogTitle>
            <DialogDescription>
              {unlockReservedTableCandidate?.blockedReason ||
                "Mesa bloqueada. Ingresa el código para tomar la mesa."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <div className="text-sm font-medium text-foreground">
              Código de desbloqueo
            </div>
            <Input
              type="password"
              inputMode="numeric"
              value={unlockReservedTableCodeText}
              onChange={(e) => setUnlockReservedTableCodeText(e.target.value)}
              placeholder={(() => {
                if (!unlockReservedTableCandidate) return "Ej: 123456";
                const isReservationBlocked = (
                  unlockReservedTableCandidate.blockedReason ?? ""
                ).startsWith("Reservada");
                if (!isReservationBlocked) return "Código admin";
                const hint = reservationsData.find(
                  (r) =>
                    r.tableId === unlockReservedTableCandidate.id &&
                    r.status === "scheduled",
                )?.unlockCodeHint;
                return hint ? `Ej: termina en ${hint}` : "Ej: 123456";
              })()}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setUnlockReservedTableCandidate(null);
                setUnlockReservedTableCodeText("");
              }}
            >
              Cancelar
            </Button>
            {unlockReservedTableCandidate &&
              (unlockReservedTableCandidate.blockedReason ?? "").startsWith(
                "Reservada",
              ) && (
                <Button
                  variant="outline"
                  onClick={() => {
                    const upcoming = getUpcomingReservationForTable(
                      unlockReservedTableCandidate.id,
                    );
                    setSelectedTableId(unlockReservedTableCandidate.id);
                    setSelectedReservationId(upcoming?.id ?? null);
                    setSelectedTakeawayOrderId(null);
                    setUnlockReservedTableCandidate(null);
                    setUnlockReservedTableCodeText("");
                  }}
                >
                  Agendar platos
                </Button>
              )}
            <Button
              onClick={() => void confirmUnlockReservedTable()}
              disabled={!apiEnabled}
            >
              Tomar mesa
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      

      <Dialog
        open={Boolean(reservationActionCandidate)}
        onOpenChange={(open) => {
          if (!open) setReservationActionCandidate(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {reservationActionCandidate
                ? `Mesa ${reservationActionCandidate.number}`
                : "Mesa"}
            </DialogTitle>
            <DialogDescription>
              Elige la acción para la mesa seleccionada.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setReservationActionCandidate(null)}
            >
              Cancelar
            </Button>
            {reservationForActionTable && (
              <Button
                variant="outline"
                onClick={() => {
                  if (!reservationActionCandidate) return;
                  setSelectedTableId(reservationActionCandidate.id);
                  setSelectedReservationId(reservationForActionTable.id);
                  setReservationActionCandidate(null);
                }}
              >
                Comanda reserva
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => {
                if (!reservationActionCandidate) return;
                setOpenTableCandidate(reservationActionCandidate);
                setOpenTableDinersText("");
                setReservationActionCandidate(null);
              }}
            >
              Abrir mesa
            </Button>
            <Button
              onClick={() => {
                if (!reservationActionCandidate) return;
                startReservationForTable(reservationActionCandidate);
                setReservationActionCandidate(null);
              }}
            >
              Reservar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isReservationModalOpen || Boolean(reserveTableCandidate)}
        onOpenChange={(open) => {
          if (!open) {
            setReserveTableCandidate(null);
            setIsReservationModalOpen(false);
          }
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {reservationIsTableless
                ? "Reservar sin mesa"
                : reserveTableCandidate
                ? `Reservar mesa ${reserveTableCandidate.number}`
                : "Reservar mesa"}
            </DialogTitle>
            <DialogDescription>
              Completa los datos para registrar la reserva.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {reservationIsTableless ? (
              <div className="flex items-center gap-2 sm:col-span-2 bg-amber-500/10 text-amber-500 p-2.5 rounded-lg border border-amber-500/20 text-xs font-medium">
                <Badge variant="outline" className="bg-amber-500/20 text-amber-400 border-amber-500/30 font-bold shrink-0">
                  Reserva Sin Mesa
                </Badge>
                <span>El cliente asignará cualquier mesa desocupada al llegar al restaurante.</span>
              </div>
            ) : reserveTableCandidate ? (
              <div className="flex items-center gap-2 sm:col-span-2 bg-primary/10 text-primary p-2.5 rounded-lg border border-primary/20 text-xs font-medium">
                <Badge variant="outline" className="bg-primary/20 text-primary border-primary/30 font-bold shrink-0">
                  Mesa {reserveTableCandidate.number}
                </Badge>
                <span>Reserva asignada a Mesa {reserveTableCandidate.number} ({reserveTableCandidate.zone ?? 'Salón Main'}).</span>
              </div>
            ) : null}

            <div className="space-y-1">
              <div className="text-sm font-medium text-foreground">
                Quien reserva
              </div>
              <Input
                value={reservationReserverName}
                onChange={(e) => setReservationReserverName(e.target.value)}
                placeholder="Nombre del cliente"
              />
            </div>
            <div className="space-y-1">
              <div className="text-sm font-medium text-foreground">
                Comensales
              </div>
              <Input
                type="number"
                inputMode="numeric"
                min={1}
                value={reservationPartySizeText}
                onChange={(e) => setReservationPartySizeText(e.target.value)}
                placeholder="Ej: 4"
              />
            </div>
            <div className="space-y-1">
              <div className="text-sm font-medium text-foreground">
                Fecha y hora
              </div>
              <Input
                type="datetime-local"
                value={reservationReservedAtText}
                onChange={(e) => setReservationReservedAtText(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <div className="text-sm font-medium text-foreground">
                Bloqueo previo (min)
              </div>
              <Input
                type="number"
                inputMode="numeric"
                min={0}
                max={1440}
                value={reservationPreBlockMinutesText}
                onChange={(e) =>
                  setReservationPreBlockMinutesText(e.target.value)
                }
                placeholder="Ej: 15"
                disabled={reservationIsTableless}
              />
            </div>
            <div className="space-y-1">
              <div className="text-sm font-medium text-foreground">
                Cocina (min antes)
              </div>
              <Input
                type="number"
                inputMode="numeric"
                min={0}
                max={1440}
                value={reservationKitchenLeadMinutesText}
                onChange={(e) =>
                  setReservationKitchenLeadMinutesText(e.target.value)
                }
                placeholder="Ej: 20"
                disabled={reservationSendMode === "manual"}
              />
            </div>
            <div className="space-y-1">
              <div className="text-sm font-medium text-foreground">
                Envío a estación
              </div>
              <select
                className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={reservationSendMode}
                onChange={(e) =>
                  setReservationSendMode(
                    e.target.value as Reservation["sendMode"],
                  )
                }
              >
                <option value="auto">Automático (según tiempo)</option>
                <option value="manual">Manual (aprueba admin)</option>
              </select>
            </div>





            <div className="space-y-1">
              <div className="text-sm font-medium text-foreground">
                Teléfono (opcional)
              </div>
              <Input
                value={reservationReserverPhone}
                onChange={(e) => setReservationReserverPhone(e.target.value)}
                placeholder="+56..."
              />
            </div>
            <div className="space-y-1">
              <div className="text-sm font-medium text-foreground">
                Código de desbloqueo
              </div>
              <Input
                value={reservationUnlockCodeText}
                onChange={(e) => setReservationUnlockCodeText(e.target.value)}
                placeholder="Ej: 123456"
              />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <div className="text-sm font-medium text-foreground">
                Email (opcional)
              </div>
              <Input
                value={reservationReserverEmail}
                onChange={(e) => setReservationReserverEmail(e.target.value)}
                placeholder="correo@dominio.cl"
              />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <div className="text-sm font-medium text-foreground">
                Notas (opcional)
              </div>
              <Textarea
                value={reservationNotes}
                onChange={(e) => setReservationNotes(e.target.value)}
                placeholder="Observaciones"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setReserveTableCandidate(null);
                setIsReservationModalOpen(false);
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={() => void confirmCreateReservation()}
              disabled={!apiEnabled || reservationSubmitting}
            >
              {reservationSubmitting ? "Confirmando..." : "Confirmar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal para Asignar Mesa a Reserva Sin Mesa */}
      <Dialog
        open={Boolean(assignTableReservationCandidate)}
        onOpenChange={(open) => {
          if (!open) setAssignTableReservationCandidate(null);
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Asignar Mesa a Reserva</DialogTitle>
            <DialogDescription>
              Selecciona una mesa para {assignTableReservationCandidate?.reserverName}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto pr-1">
            {availableTablesByZone.length === 0 ? (
              <div className="text-sm text-muted-foreground text-center py-6">
                No hay mesas disponibles en este momento.
              </div>
            ) : (
              availableTablesByZone.map(({ zone, tables: zoneTables }) => (
                <div key={zone} className="space-y-2">
                  <div className="flex items-center gap-2 pb-1 border-b border-border">
                    <span className="h-2 w-2 rounded-full bg-primary" />
                    <span className="text-xs font-semibold uppercase tracking-wider text-foreground">
                      {zone}
                    </span>
                    <span className="text-[10px] text-muted-foreground bg-muted px-2 py-0.5 rounded-full font-mono">
                      {zoneTables.length} {zoneTables.length === 1 ? "mesa" : "mesas"}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {zoneTables.map((t) => {
                      const isOccupied = t.status === "occupied" || t.status === "pending-payment" || Boolean(t.currentOrderId);
                      const isBlocked = t.status === "blocked" && !isOccupied;

                      return (
                        <Button
                          key={t.id}
                          variant="outline"
                          className={cn(
                            "flex flex-col items-center justify-center p-3 h-auto transition-all active:scale-95 text-left",
                            isOccupied
                              ? "bg-amber-500/10 border-amber-500/30 hover:border-amber-500 hover:bg-amber-500/20"
                              : isBlocked
                              ? "bg-red-500/10 border-red-500/30 hover:border-red-400"
                              : "hover:bg-primary/10 hover:border-primary"
                          )}
                          onClick={() => {
                            if (!assignTableReservationCandidate) return;
                            if (isOccupied || isBlocked) {
                              setConfirmOccupiedTableInPos(t);
                            } else {
                              void handleConfirmAssignReservationInPos(t);
                            }
                          }}
                        >
                          <div className="flex items-center justify-between w-full">
                            <span className="font-bold text-base">Mesa {t.number}</span>
                            {isOccupied ? (
                              <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-500 font-medium">
                                {t.status === "pending-payment" ? "Cuenta" : "Ocupada"}
                              </span>
                            ) : isBlocked ? (
                              <span className="text-[9px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-400 font-medium">
                                Bloqueada
                              </span>
                            ) : (
                              <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-400 font-medium">
                                Libre
                              </span>
                            )}
                          </div>
                          <span className="text-xs text-muted-foreground mt-1 w-full text-left">
                            {isOccupied
                              ? `Ocupada ${t.waiter ? `· ${t.waiter}` : ''}`
                              : `Cap: ${t.capacity} pers`}
                          </span>
                        </Button>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAssignTableReservationCandidate(null)}
            >
              Cancelar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation Modal for Occupied Table Assignment in POS */}
      <AlertDialog
        open={Boolean(confirmOccupiedTableInPos)}
        onOpenChange={(open) => !open && setConfirmOccupiedTableInPos(null)}
      >
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              <span>¿Sumar reserva a Mesa {confirmOccupiedTableInPos?.number}?</span>
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2.5 text-xs mt-2">
              <span className="block">
                La <strong>Mesa {confirmOccupiedTableInPos?.number}</strong> ya tiene una comanda activa en curso.
              </span>
              <span className="block bg-muted p-3 rounded-lg border space-y-1">
                <span className="block font-medium">
                  Reserva: {assignTableReservationCandidate?.reserverName}
                </span>
                <span className="block text-muted-foreground">
                  • {assignTableReservationCandidate?.partySize} personas
                  {assignTableReservationCandidate?.items?.length ? ` · ${assignTableReservationCandidate.items.length} productos pre-guardados` : ''}
                </span>
              </span>
              <span className="block text-muted-foreground">
                Al confirmar, el contenido y los comensales de la reserva se **sumarán automáticamente** a la comanda activa de la Mesa {confirmOccupiedTableInPos?.number}.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 mt-3">
            <AlertDialogCancel onClick={() => setConfirmOccupiedTableInPos(null)}>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmOccupiedTableInPos) {
                  void handleConfirmAssignReservationInPos(confirmOccupiedTableInPos);
                }
              }}
              className="bg-amber-500 hover:bg-amber-600 text-black font-semibold"
            >
              Confirmar y Sumar a Comanda
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={Boolean(editReservationCandidate)}
        onOpenChange={(open) => {
          if (!open) setEditReservationCandidate(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editReservationCandidate
                ? `Editar reserva · Mesa ${editReservationCandidate.tableNumber}`
                : "Editar reserva"}
            </DialogTitle>
            <DialogDescription>
              Ajusta los datos de la reserva o anúlala desde el listado.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <div className="text-sm font-medium text-foreground">
                Quien reserva
              </div>
              <Input
                value={editReservationReserverName}
                onChange={(e) => setEditReservationReserverName(e.target.value)}
                placeholder="Nombre"
              />
            </div>
            <div className="space-y-1">
              <div className="text-sm font-medium text-foreground">
                Comensales
              </div>
              <Input
                type="number"
                inputMode="numeric"
                min={1}
                value={editReservationPartySizeText}
                onChange={(e) =>
                  setEditReservationPartySizeText(e.target.value)
                }
                placeholder="Ej: 4"
              />
            </div>
            <div className="space-y-1">
              <div className="text-sm font-medium text-foreground">
                Fecha y hora
              </div>
              <Input
                type="datetime-local"
                value={editReservationReservedAtText}
                onChange={(e) =>
                  setEditReservationReservedAtText(e.target.value)
                }
              />
            </div>
            <div className="space-y-1">
              <div className="text-sm font-medium text-foreground">
                Bloqueo previo (min)
              </div>
              <Input
                type="number"
                inputMode="numeric"
                min={0}
                max={1440}
                value={editReservationPreBlockMinutesText}
                onChange={(e) =>
                  setEditReservationPreBlockMinutesText(e.target.value)
                }
                placeholder="Ej: 15"
              />
            </div>
            <div className="space-y-1">
              <div className="text-sm font-medium text-foreground">
                Cocina (min antes)
              </div>
              <Input
                type="number"
                inputMode="numeric"
                min={0}
                max={1440}
                value={editReservationKitchenLeadMinutesText}
                onChange={(e) =>
                  setEditReservationKitchenLeadMinutesText(e.target.value)
                }
                placeholder="Ej: 20"
                disabled={editReservationSendMode === "manual"}
              />
            </div>
            <div className="space-y-1">
              <div className="text-sm font-medium text-foreground">
                Envío a estación
              </div>
              <select
                className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={editReservationSendMode}
                onChange={(e) =>
                  setEditReservationSendMode(
                    e.target.value as Reservation["sendMode"],
                  )
                }
              >
                <option value="auto">Automático (según tiempo)</option>
                <option value="manual">Manual (aprueba admin)</option>
              </select>
            </div>

            <div className="space-y-1">
              <div className="text-sm font-medium text-foreground">
                Teléfono (opcional)
              </div>
              <Input
                value={editReservationReserverPhone}
                onChange={(e) =>
                  setEditReservationReserverPhone(e.target.value)
                }
                placeholder="+56..."
              />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <div className="text-sm font-medium text-foreground">
                Email (opcional)
              </div>
              <Input
                value={editReservationReserverEmail}
                onChange={(e) =>
                  setEditReservationReserverEmail(e.target.value)
                }
                placeholder="correo@dominio.cl"
              />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <div className="text-sm font-medium text-foreground">
                Notas (opcional)
              </div>
              <Textarea
                value={editReservationNotes}
                onChange={(e) => setEditReservationNotes(e.target.value)}
                placeholder="Observaciones"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditReservationCandidate(null)}
            >
              Cancelar
            </Button>
            <Button
              onClick={() => void confirmUpdateReservation()}
              disabled={!apiEnabled || reservationSubmitting}
            >
              {reservationSubmitting ? "Guardando..." : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CancelReservationModal
        reservation={cancelReservationCandidate}
        isOpen={Boolean(cancelReservationCandidate)}
        onClose={() => setCancelReservationCandidate(null)}
        onConfirm={confirmCancelReservation}
        disabled={!apiEnabled}
        isSubmitting={reservationSubmitting}
      />

      <Dialog
        open={Boolean(openTableCandidate)}
        onOpenChange={(open) => {
          if (!open) setOpenTableCandidate(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {openTableCandidate
                ? `Abrir mesa ${openTableCandidate.number}`
                : "Abrir mesa"}
            </DialogTitle>
            <DialogDescription>
              {openTableCandidate
                ? `Capacidad máxima: ${openTableCandidate.capacity}. Puedes registrar el número de comensales (opcional).`
                : "Puedes registrar el número de comensales (opcional)."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <div className="text-sm font-medium text-foreground">
              Comensales (opcional)
            </div>
            <Input
              type="number"
              inputMode="numeric"
              min={1}
              value={openTableDinersText}
              onChange={(e) => setOpenTableDinersText(e.target.value)}
              placeholder="Ej: 2"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setOpenTableCandidate(null);
                setOpenTableDinersText("");
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={() => {
                if (!openTableCandidate) return;
                confirmOpenTable(openTableCandidate, openTableDinersText);
              }}
            >
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
              open={Boolean(openTakeawayClient)}
              onOpenChange={(open) => {
                if (!open) setOpenTakeawayClient(false);
              }}
            >
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>
                    Ingrese nombre del cliente
                  </DialogTitle>
                  
                </DialogHeader>
                <div className="space-y-2">
                  <div className="text-sm font-medium text-foreground">
                    Nombre
                  </div>
                  <Input
                    type="text"
                    inputMode="numeric"
                    min={1}
                    value={takeawayClientName}
                    onChange={(e) => setTakeawayClientName(e.target.value)}
                    placeholder="Ej: Lucas"
                  />
                </div>
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setOpenTakeawayClient(false);
                      setTakeawayClientName("");
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    onClick={() => {
                      if (takeawayClientName === "") return;
                      confirmTakeAway(takeawayClientName);
                    }}
                  >
                    Confirmar
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

      <Dialog
        open={Boolean(transferOriginTable)}
        onOpenChange={(open) => {
          if (!open) {
            setTransferOriginTable(null);
            setTransferDestinationTableId("");
          }
        }}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Trasladar Mesa {transferOriginTable?.number}</DialogTitle>
            <DialogDescription>
              Seleccione la mesa destino. Se trasladarán todos los productos, tiempos y el estado actual de la mesa origen, y esta se liberará.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1">
              <div className="text-sm font-medium text-foreground">
                Zona
              </div>
              <select
                className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={transferSelectedZone}
                onChange={(e) => {
                  setTransferSelectedZone(e.target.value);
                  setTransferDestinationTableId("");
                }}
              >
                <option value="">Seleccione una zona...</option>
                {derivedZones.map((zone) => (
                  <option key={zone} value={zone}>
                    {zone}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <div className="text-sm font-medium text-foreground">
                Mesa Destino
              </div>
              <select
                className="h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={transferDestinationTableId}
                onChange={(e) => setTransferDestinationTableId(e.target.value)}
                disabled={!transferSelectedZone}
              >
                <option value="">Seleccione una mesa disponible...</option>
                {tablesData
                  .filter(
                    (t) =>
                      t.status === "available" &&
                      t.id !== transferOriginTable?.id &&
                      t.zone === transferSelectedZone
                  )
                  .map((t) => (
                    <option key={t.id} value={t.id}>
                      Mesa {t.number} (Capacidad: {t.capacity})
                    </option>
                  ))}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              disabled={isTransferring}
              onClick={() => {
                setTransferOriginTable(null);
                setTransferDestinationTableId("");
              }}
            >
              Cancelar
            </Button>
            <Button
              disabled={!transferDestinationTableId || isTransferring}
              onClick={handleTransferTableConfirm}
            >
              {isTransferring ? "Trasladando..." : "Confirmar Traslado"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </POSLayout>
  );
}
